import React, { useState, useEffect, useRef, useCallback, useMemo, memo } from 'react';
import { DashboardLayout } from '../../components/DashboardLayout';
import { Card, CardContent, CardHeader, CardTitle } from '../../components/ui/card';
import { Button } from '../../components/ui/button';
import { Input } from '../../components/ui/input';
import { Label } from '../../components/ui/label';
import { Badge } from '../../components/ui/badge';
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogFooter, DialogDescription } from '../../components/ui/dialog';
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from '../../components/ui/select';
import { Collapsible, CollapsibleContent, CollapsibleTrigger } from '../../components/ui/collapsible';
import { BarcodeScanner } from '../../components/BarcodeScanner';
import { useAuth } from '../../contexts/AuthContext';
import { useNavigate } from 'react-router-dom';
import { resolveProductImage } from '../../hooks/useProductImage';
import axios from 'axios';
import { toast } from 'sonner';

// ── Produktname-Autocomplete Komponente ──────────────────────────────────────
const ProductNameAutocomplete = ({ value, onChange, onSelectSuggestion, locationId, placeholder = "Produktname" }) => {
  const [suggestions, setSuggestions] = useState([]);
  const [showDropdown, setShowDropdown] = useState(false);
  const [loading, setLoading] = useState(false);
  const debounceRef = useRef(null);
  const wrapperRef = useRef(null);
  const API = process.env.REACT_APP_BACKEND_URL;

  // Dropdown schließen bei Klick außerhalb
  useEffect(() => {
    const handleClickOutside = (e) => {
      if (wrapperRef.current && !wrapperRef.current.contains(e.target)) {
        setShowDropdown(false);
      }
    };
    document.addEventListener('mousedown', handleClickOutside);
    return () => document.removeEventListener('mousedown', handleClickOutside);
  }, []);

  const fetchSuggestions = useCallback(async (q) => {
    if (q.length < 2) { setSuggestions([]); setShowDropdown(false); return; }
    setLoading(true);
    try {
      const token = localStorage.getItem('token');
      const params = { q };
      if (locationId) params.location_id = locationId;
      const res = await axios.get(`${API}/api/mhd/product-suggestions`, {
        headers: { Authorization: `Bearer ${token}` },
        params,
      });
      const items = res.data?.suggestions || [];
      setSuggestions(items);
      setShowDropdown(items.length > 0);
    } catch { /* silent */ } finally { setLoading(false); }
  }, [API, locationId]);

  const handleChange = (e) => {
    const v = e.target.value;
    onChange(v);
    clearTimeout(debounceRef.current);
    debounceRef.current = setTimeout(() => fetchSuggestions(v), 280);
  };

  const handleSelect = (s) => {
    onChange(s.name);
    setShowDropdown(false);
    setSuggestions([]);
    if (onSelectSuggestion) onSelectSuggestion(s);
  };

  // Query-Term im Namen hervorheben
  const highlight = (text, q) => {
    if (!q || q.length < 2) return text;
    const idx = text.toLowerCase().indexOf(q.toLowerCase());
    if (idx === -1) return text;
    return (
      <>
        {text.slice(0, idx)}
        <strong className="text-blue-600 dark:text-blue-400">{text.slice(idx, idx + q.length)}</strong>
        {text.slice(idx + q.length)}
      </>
    );
  };

  return (
    <div ref={wrapperRef} className="relative">
      <Input
        placeholder={placeholder}
        value={value}
        onChange={handleChange}
        onFocus={() => suggestions.length > 0 && setShowDropdown(true)}
        autoComplete="off"
        data-testid="product-name-autocomplete"
      />
      {loading && (
        <div className="absolute right-3 top-1/2 -translate-y-1/2">
          <div className="h-3.5 w-3.5 border-2 border-blue-400 border-t-transparent rounded-full animate-spin" />
        </div>
      )}
      {showDropdown && suggestions.length > 0 && (
        <div className="absolute z-50 w-full mt-1 bg-white dark:bg-gray-900 border border-gray-200 dark:border-gray-700 rounded-xl shadow-xl max-h-60 overflow-y-auto">
          {suggestions.map((s, i) => (
            <button
              key={i}
              type="button"
              className="w-full text-left px-3 py-2.5 hover:bg-blue-50 dark:hover:bg-blue-900/20 transition-colors flex items-center justify-between gap-2 text-sm border-b border-gray-100 dark:border-gray-800 last:border-0"
              onMouseDown={(e) => { e.preventDefault(); handleSelect(s); }}
            >
              <span className="truncate">{highlight(s.name, value)}</span>
              {s.ean && (
                <span className="text-xs text-muted-foreground font-mono shrink-0 bg-muted/50 px-1.5 py-0.5 rounded">
                  {s.ean}
                </span>
              )}
            </button>
          ))}
        </div>
      )}
    </div>
  );
};
import { 
  Package, 
  Plus, 
  Camera, 
  Trash2, 
  AlertTriangle, 
  CheckCircle2,
  CheckCircle,
  Clock,
  Barcode,
  X,
  Download,
  Settings,
  FileText,
  Loader2,
  Hash,
  Search,
  ChevronDown,
  ChevronUp,
  ChevronLeft,
  ChevronRight,
  Check,
  Archive,
  AlertCircle,
  CalendarDays,
  Pencil,
  Image as ImageIcon,
  ZoomIn,
  Link as LinkIcon,
  RefreshCw,
} from 'lucide-react';

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;

// Image Lightbox Component for full-size view - Memoized
const ImageLightbox = memo(({ src, alt, isOpen, onClose }) => {
  if (!isOpen) return null;
  
  return (
    <div 
      className="fixed inset-0 z-50 bg-black/80 flex items-center justify-center p-4"
      onClick={onClose}
    >
      <div className="relative max-w-4xl max-h-[90vh]">
        <button
          onClick={onClose}
          className="absolute -top-10 right-0 text-white hover:text-gray-300"
        >
          <X className="h-8 w-8" />
        </button>
        <img 
          src={src} 
          alt={alt}
          className="max-w-full max-h-[85vh] object-contain rounded-lg"
          onClick={(e) => e.stopPropagation()}
        />
      </div>
    </div>
  );
});

// Product Image Preview Component - Clickable & Editable
const ProductImagePreview = ({ imageUrl, onChangePhoto, onChangeUrl, onRemove }) => {
  const [lightboxOpen, setLightboxOpen] = useState(false);
  const [showUrlInput, setShowUrlInput] = useState(false);
  const [urlValue, setUrlValue] = useState('');

  if (!imageUrl) return null;

  const handleUrlSubmit = () => {
    if (urlValue.trim()) {
      onChangeUrl(urlValue.trim());
      setShowUrlInput(false);
      setUrlValue('');
    }
  };

  return (
    <>
      <div className="space-y-2">
        <Label className="text-sm flex items-center gap-2">
          <ImageIcon className="h-4 w-4" />
          Produktbild
        </Label>
        <div className="flex items-start gap-3 p-3 bg-gray-50 dark:bg-gray-800 rounded-lg border dark:border-gray-700">
          {/* Clickable Image for Lightbox */}
          <div 
            className="relative cursor-pointer group"
            onClick={() => setLightboxOpen(true)}
          >
            <img 
              src={imageUrl} 
              alt="Produkt"
              className="w-16 h-16 object-cover rounded-lg border dark:border-gray-700 bg-white dark:bg-gray-900"
              onError={(e) => { e.target.src = ''; e.target.style.display = 'none'; }}
            />
            <div className="absolute inset-0 bg-black/0 group-hover:bg-black/30 rounded-lg transition-all flex items-center justify-center">
              <ZoomIn className="h-5 w-5 text-white opacity-0 group-hover:opacity-100 transition-opacity" />
            </div>
          </div>
          
          {/* Action Buttons */}
          <div className="flex-1 space-y-1">
            <p className="text-sm text-green-600 dark:text-green-400 font-medium">✓ Bild vorhanden</p>
            <div className="flex flex-wrap gap-1">
              <Button 
                variant="outline" 
                size="sm" 
                className="h-7 text-xs"
                onClick={onChangePhoto}
              >
                <Camera className="h-3 w-3 mr-1" />
                Neues Foto
              </Button>
              <Button 
                variant="outline" 
                size="sm" 
                className="h-7 text-xs"
                onClick={() => setShowUrlInput(!showUrlInput)}
              >
                <LinkIcon className="h-3 w-3 mr-1" />
                URL
              </Button>
              <Button 
                variant="ghost" 
                size="sm" 
                className="h-7 text-xs text-red-600 hover:text-red-700 hover:bg-red-50"
                onClick={onRemove}
              >
                <X className="h-3 w-3 mr-1" />
                Entfernen
              </Button>
            </div>
          </div>
        </div>
        
        {/* URL Input */}
        {showUrlInput && (
          <div className="flex gap-2">
            <Input
              placeholder="https://example.com/bild.jpg"
              value={urlValue}
              onChange={(e) => setUrlValue(e.target.value)}
              className="text-sm"
            />
            <Button size="sm" onClick={handleUrlSubmit}>OK</Button>
          </div>
        )}
      </div>

      {/* Lightbox */}
      <ImageLightbox 
        src={imageUrl}
        alt="Produktbild"
        isOpen={lightboxOpen}
        onClose={() => setLightboxOpen(false)}
      />
    </>
  );
};

// Scroll Date Picker Component
// ── Kalender-Datumspicker (Bild-2-Stil: Monatsraster mit < > Navigation) ─────
const CalendarDatePicker = ({ value, onChange }) => {
  const today = new Date();
  today.setHours(0, 0, 0, 0);

  const initDate = () => {
    if (value) {
      const d = new Date(value + 'T00:00:00');
      if (!isNaN(d)) return { month: d.getMonth(), year: d.getFullYear() };
    }
    return { month: today.getMonth(), year: today.getFullYear() };
  };

  const selectedDate = value ? new Date(value + 'T00:00:00') : null;
  const [viewMonth, setViewMonth] = useState(() => initDate().month);
  const [viewYear, setViewYear] = useState(() => initDate().year);
  const [open, setOpen] = useState(false);
  const [pending, setPending] = useState(selectedDate);

  useEffect(() => {
    if (value) {
      const d = new Date(value + 'T00:00:00');
      if (!isNaN(d)) {
        setPending(d);
        setViewMonth(d.getMonth());
        setViewYear(d.getFullYear());
      }
    }
  }, [value]);

  const months = ['Januar', 'Februar', 'März', 'April', 'Mai', 'Juni',
                  'Juli', 'August', 'September', 'Oktober', 'November', 'Dezember'];
  const weekDays = ['Mo', 'Di', 'Mi', 'Do', 'Fr', 'Sa', 'So'];

  const getDaysInMonth = (m, y) => new Date(y, m + 1, 0).getDate();
  // Wochentag des 1. des Monats (0=Mo,...,6=So)
  const getFirstWeekday = (m, y) => { const d = new Date(y, m, 1).getDay(); return d === 0 ? 6 : d - 1; };

  const prevMonth = () => {
    if (viewMonth === 0) { setViewMonth(11); setViewYear(y => y - 1); }
    else setViewMonth(m => m - 1);
  };
  const nextMonth = () => {
    if (viewMonth === 11) { setViewMonth(0); setViewYear(y => y + 1); }
    else setViewMonth(m => m + 1);
  };

  const handleDayClick = (day) => {
    const d = new Date(viewYear, viewMonth, day);
    setPending(d);
  };

  const handleConfirm = () => {
    if (pending) {
      const y = pending.getFullYear();
      const m = String(pending.getMonth() + 1).padStart(2, '0');
      const d = String(pending.getDate()).padStart(2, '0');
      onChange(`${y}-${m}-${d}`);
    }
    setOpen(false);
  };

  const handleReset = () => {
    const tomorrow = new Date();
    tomorrow.setDate(tomorrow.getDate() + 1);
    setPending(tomorrow);
    setViewMonth(tomorrow.getMonth());
    setViewYear(tomorrow.getFullYear());
  };

  const formatDisplay = (d) => {
    if (!d) return 'Datum wählen';
    return d.toLocaleDateString('de-DE', { day: '2-digit', month: 'long', year: 'numeric' });
  };

  const daysInMonth = getDaysInMonth(viewMonth, viewYear);
  const firstWd = getFirstWeekday(viewMonth, viewYear);
  const currentYear = new Date().getFullYear();

  return (
    <>
      {/* Trigger-Button */}
      <button
        type="button"
        onClick={() => setOpen(true)}
        className="w-full flex items-center justify-between px-4 py-3 rounded-xl border-2 border-primary bg-primary/5 hover:bg-primary/10 transition-colors text-left"
      >
        <span className="font-semibold text-base text-foreground">
          {formatDisplay(selectedDate)}
        </span>
        <CalendarDays className="h-5 w-5 text-primary shrink-0" />
      </button>

      {/* Kalender-Overlay */}
      {open && (
        <div
          className="fixed inset-0 z-[9999] flex items-end sm:items-center justify-center bg-black/50"
          onClick={() => setOpen(false)}
        >
          <div
            className="bg-background rounded-2xl shadow-2xl p-5 w-full max-w-sm mx-4 mb-4 sm:mb-0"
            onClick={e => e.stopPropagation()}
          >
            {/* Header: Monat + Navigation */}
            <div className="flex items-center justify-between mb-4">
              <button
                type="button"
                onClick={prevMonth}
                className="p-2 rounded-full hover:bg-muted transition-colors"
                aria-label="Vorheriger Monat"
              >
                <ChevronLeft className="h-5 w-5 text-primary" />
              </button>
              <button
                type="button"
                onClick={() => {/* Jahr-Auswahl optional */}}
                className="font-bold text-lg text-foreground"
              >
                {months[viewMonth]} {viewYear}
              </button>
              <button
                type="button"
                onClick={nextMonth}
                className="p-2 rounded-full hover:bg-muted transition-colors"
                aria-label="Nächster Monat"
              >
                <ChevronRight className="h-5 w-5 text-primary" />
              </button>
            </div>

            {/* Wochentag-Header */}
            <div className="grid grid-cols-7 mb-1">
              {weekDays.map(wd => (
                <div key={wd} className="text-center text-xs font-semibold text-muted-foreground py-1">
                  {wd}
                </div>
              ))}
            </div>

            {/* Tage-Grid */}
            <div className="grid grid-cols-7 gap-y-1">
              {/* Leer-Zellen am Anfang */}
              {Array.from({ length: firstWd }).map((_, i) => <div key={`e-${i}`} />)}
              {/* Tage */}
              {Array.from({ length: daysInMonth }, (_, i) => i + 1).map(day => {
                const thisDate = new Date(viewYear, viewMonth, day);
                const isToday = thisDate.getTime() === today.getTime();
                const isPending = pending && thisDate.toDateString() === pending.toDateString();
                const isPast = thisDate < today;
                return (
                  <button
                    key={day}
                    type="button"
                    onClick={() => handleDayClick(day)}
                    className={`
                      w-full aspect-square flex items-center justify-center rounded-full
                      text-sm font-medium transition-colors
                      ${isPending ? 'bg-primary text-primary-foreground' : ''}
                      ${isToday && !isPending ? 'border-2 border-primary text-primary' : ''}
                      ${isPast && !isPending && !isToday ? 'text-muted-foreground' : ''}
                      ${!isPending ? 'hover:bg-muted' : ''}
                    `}
                  >
                    {day}
                  </button>
                );
              })}
            </div>

            {/* Aktionen */}
            <div className="flex items-center justify-between mt-5 pt-4 border-t border-border">
              <button
                type="button"
                onClick={handleReset}
                className="px-4 py-2 rounded-xl text-sm font-medium bg-muted hover:bg-muted/80 transition-colors"
              >
                Zurücksetzen
              </button>
              <button
                type="button"
                onClick={handleConfirm}
                className="w-12 h-12 flex items-center justify-center rounded-full bg-primary text-primary-foreground shadow-lg hover:bg-primary/90 transition-colors"
                aria-label="Datum bestätigen"
              >
                <Check className="h-6 w-6" />
              </button>
            </div>
          </div>
        </div>
      )}
    </>
  );
};

// Product Card Component - Memoized for performance
const ProductCard = memo(({ product, status, onWriteOff, onEdit }) => {
  const getDaysUntilExpiry = useCallback((dateStr) => {
    const expiry = new Date(dateStr);
    const now = new Date();
    now.setHours(0, 0, 0, 0);
    return Math.ceil((expiry - now) / (1000 * 60 * 60 * 24));
  }, []);
  
  const days = getDaysUntilExpiry(product.expiry_date);
  
  const statusConfig = useMemo(() => ({
    expired: { border: 'border-red-600 dark:border-red-500', bg: 'bg-red-50 dark:bg-red-950/40', badge: 'bg-red-600' },
    today: { border: 'border-orange-500 dark:border-orange-400', bg: 'bg-orange-50 dark:bg-orange-950/40', badge: 'bg-orange-500' },
    soon: { border: 'border-yellow-500 dark:border-yellow-400', bg: 'bg-yellow-50 dark:bg-yellow-950/40', badge: 'bg-yellow-500' },
    ok: { border: 'border-green-500 dark:border-green-400', bg: 'bg-green-50 dark:bg-green-950/40', badge: 'bg-green-500' }
  }), []);
  
  const config = statusConfig[status] || statusConfig.ok;
  
  const formatDate = useCallback((dateStr) => {
    try {
      return new Date(dateStr).toLocaleDateString('de-DE');
    } catch {
      return dateStr;
    }
  }, []);
  
  const getBadgeText = useCallback(() => {
    if (days < 0) return `${Math.abs(days)} Tage überfällig`;
    if (days === 0) return 'Heute';
    return `${days} Tage`;
  }, [days]);

  const [lightboxOpen, setLightboxOpen] = useState(false);
  // 3-Tier Bildlogik: Master > OFF-URL > Kunden-Foto > null
  const token = localStorage.getItem('token') || sessionStorage.getItem('token');
  const imageUrl = resolveProductImage(product, token);

  return (
    <>
      <div className={`p-3 rounded-lg border-2 ${config.border} ${config.bg}`}>
        <div className="flex items-start gap-3">
          {imageUrl ? (
            <div 
              className="relative cursor-pointer group"
              onClick={() => setLightboxOpen(true)}
            >
              <img 
                src={imageUrl} 
                alt={product.product_name}
                className="w-14 h-14 object-cover rounded-lg border dark:border-gray-700 bg-white dark:bg-gray-900"
                onError={(e) => { e.target.style.display = 'none'; }}
              />
              <div className="absolute inset-0 bg-black/0 group-hover:bg-black/30 rounded-lg transition-all flex items-center justify-center">
                <ZoomIn className="h-5 w-5 text-white opacity-0 group-hover:opacity-100 transition-opacity" />
              </div>
            </div>
          ) : (
            <div className="w-14 h-14 bg-gray-200 dark:bg-gray-700 rounded-lg flex items-center justify-center flex-shrink-0">
              {product.has_local_photo ? (
                <Camera className="h-6 w-6 text-blue-400 dark:text-blue-500" title="Lokales Foto vorhanden" />
              ) : (
                <Package className="h-6 w-6 text-gray-400 dark:text-gray-500" />
              )}
            </div>
          )}
          
          <div className="flex-1 min-w-0">
            <h3 className="font-semibold text-sm truncate">{product.product_name}</h3>
            <p className="text-xs text-muted-foreground">EAN: {product.ean}</p>
            <div className="flex items-center gap-2 mt-1">
              <Badge className={`${config.badge} text-xs`}>
                {getBadgeText()}
              </Badge>
              <span className="text-xs">{formatDate(product.expiry_date)}</span>
            </div>
          </div>
          
          <div className="flex gap-1 flex-shrink-0">
            <Button
              variant="outline"
              size="sm"
              className="h-8 w-8 p-0"
              onClick={() => onEdit(product)}
              title="Bearbeiten"
            >
              <Pencil className="h-3.5 w-3.5" />
          </Button>
          <Button
            variant="destructive"
            size="sm"
            className="h-8 text-xs"
            onClick={() => onWriteOff(product)}
          >
            Abschreiben
          </Button>
        </div>
      </div>
    </div>
    
    {/* Image Lightbox */}
    <ImageLightbox 
      src={imageUrl}
      alt={product.product_name}
      isOpen={lightboxOpen}
      onClose={() => setLightboxOpen(false)}
    />
    </>
  );
});

// Archived Product Card (simpler, read-only) - Memoized
const ArchivedProductCard = memo(({ product, isSelected, onSelect, onWwsToggle, wwsLoading }) => {
  const formatDate = useCallback((dateStr) => {
    try {
      return new Date(dateStr).toLocaleDateString('de-DE');
    } catch {
      return dateStr;
    }
  }, []);

  const isWws = !!product.wws_abgeschrieben;

  return (
    <div
      className={`p-3 rounded-lg border transition-colors ${
        isSelected
          ? 'border-blue-400 bg-blue-50 dark:bg-blue-950/30 dark:border-blue-600'
          : isWws
          ? 'border-green-200 bg-green-50/60 dark:bg-green-950/20 dark:border-green-800'
          : 'border-gray-200 dark:border-gray-700 bg-gray-50 dark:bg-gray-800'
      }`}
      data-testid={`archived-card-${product.id}`}
    >
      <div className="flex items-start gap-3">
        {/* Checkbox */}
        <input
          type="checkbox"
          checked={isSelected}
          onChange={() => onSelect(product.id)}
          className="mt-1 h-4 w-4 cursor-pointer accent-blue-600 flex-shrink-0"
          data-testid={`archived-checkbox-${product.id}`}
        />
        <div className="w-8 h-8 bg-gray-200 dark:bg-gray-700 rounded flex items-center justify-center flex-shrink-0">
          <Archive className="h-4 w-4 text-gray-400 dark:text-gray-500" />
        </div>
        <div className="flex-1 min-w-0">
          <div className="flex items-center gap-2 flex-wrap">
            <h3 className="font-medium text-sm truncate">{product.product_name}</h3>
            {isWws ? (
              <span className="inline-flex items-center gap-1 text-xs font-medium px-1.5 py-0.5 rounded-full bg-green-100 text-green-700 dark:bg-green-900/40 dark:text-green-400 border border-green-200 dark:border-green-700">
                <CheckCircle className="h-3 w-3" /> Im WWS
              </span>
            ) : (
              <span className="inline-flex items-center gap-1 text-xs font-medium px-1.5 py-0.5 rounded-full bg-orange-100 text-orange-700 dark:bg-orange-900/40 dark:text-orange-400 border border-orange-200 dark:border-orange-700">
                <Clock className="h-3 w-3" /> Ausstehend
              </span>
            )}
          </div>
          <p className="text-xs text-muted-foreground">
            {product.quantity} Stk • Art.Nr: {product.article_number || '-'}
          </p>
          <p className="text-xs text-muted-foreground">
            Abgeschrieben: {formatDate(product.written_off_at)}
            {isWws && product.wws_abgeschrieben_by_name && (
              <span className="ml-2 text-green-600 dark:text-green-400">
                • WWS: {formatDate(product.wws_abgeschrieben_at)} von {product.wws_abgeschrieben_by_name}
              </span>
            )}
          </p>
        </div>
        {/* Toggle button */}
        <button
          onClick={() => onWwsToggle(product.id, !isWws)}
          disabled={wwsLoading}
          title={isWws ? 'WWS-Status zurücksetzen' : 'Als Im WWS abgeschrieben markieren'}
          className={`flex-shrink-0 p-1.5 rounded-full border transition-all ${
            isWws
              ? 'border-green-300 bg-green-100 text-green-700 hover:bg-red-100 hover:border-red-300 hover:text-red-600 dark:bg-green-900/30 dark:border-green-700 dark:text-green-400'
              : 'border-gray-300 bg-white text-gray-400 hover:bg-green-100 hover:border-green-400 hover:text-green-700 dark:bg-gray-700 dark:border-gray-600 dark:hover:bg-green-900/30'
          }`}
          data-testid={`wws-toggle-${product.id}`}
        >
          {isWws ? <CheckCircle className="h-4 w-4" /> : <CheckCircle2 className="h-4 w-4" />}
        </button>
      </div>
    </div>
  );
});

// Collapsible Category Section - Memoized
const CategorySection = memo(({ title, icon: Icon, count, color, bgColor, children, defaultOpen = false }) => {
  const [isOpen, setIsOpen] = useState(defaultOpen);
  
  return (
    <Collapsible open={isOpen} onOpenChange={setIsOpen}>
      <CollapsibleTrigger asChild>
        <div className={`flex items-center justify-between p-4 rounded-lg cursor-pointer transition-colors ${bgColor} border`}>
          <div className="flex items-center gap-3">
            <Icon className={`h-5 w-5 ${color}`} />
            <span className={`font-semibold ${color}`}>{title}</span>
          </div>
          <div className="flex items-center gap-2">
            <Badge variant="secondary" className="font-bold">{count}</Badge>
            {isOpen ? (
              <ChevronUp className="h-5 w-5 text-gray-500 dark:text-gray-400" />
            ) : (
              <ChevronDown className="h-5 w-5 text-gray-500 dark:text-gray-400" />
            )}
          </div>
        </div>
      </CollapsibleTrigger>
      <CollapsibleContent>
        <div className="space-y-2 mt-2 pl-2">
          {children}
        </div>
      </CollapsibleContent>
    </Collapsible>
  );
});

// ─── Android Kamera-Helpers (außerhalb der Komponente) ─────────────────────
// Komprimiert ein base64-Bild auf max. 800px Breite / JPEG 0.72 Qualität (~10x kleiner)
const compressImage = (dataUrl, maxWidth = 800, quality = 0.72) =>
  new Promise((resolve) => {
    const img = new Image();
    img.onload = () => {
      let { width, height } = img;
      if (width > maxWidth) { height = Math.round((height * maxWidth) / width); width = maxWidth; }
      const canvas = document.createElement('canvas');
      canvas.width = width; canvas.height = height;
      canvas.getContext('2d').drawImage(img, 0, 0, width, height);
      resolve(canvas.toDataURL('image/jpeg', quality));
    };
    img.onerror = () => resolve(dataUrl); // fallback: Original behalten
    img.src = dataUrl;
  });

const MHD_DRAFT_KEY = 'mhd_form_draft';
const MHD_CAMERA_KEY = 'mhd_camera_active';
// ───────────────────────────────────────────────────────────────────────────

export const MHDControl = () => {
  const { user, loading: authLoading } = useAuth();
  const navigate = useNavigate();
  const isAdmin = user?.role === 'admin';
  
  const [products, setProducts] = useState([]);
  const [writeOffs, setWriteOffs] = useState([]);
  const [locations, setLocations] = useState([]);
  const [selectedLocation, setSelectedLocation] = useState('all');
  // WWS-Status State
  const [selectedWriteoffs, setSelectedWriteoffs] = useState(new Set());
  const [wwsLoading, setWwsLoading] = useState(false);
  const [settings, setSettings] = useState({ warning_days: 7 });
  const [loading, setLoading] = useState(true);
  
  // Pagination state
  const [currentPage, setCurrentPage] = useState(1);
  const [totalProducts, setTotalProducts] = useState(0);
  const [totalPages, setTotalPages] = useState(1);
  const PAGE_SIZE = 100;
  
  // Dialogs
  const [addDialogOpen, setAddDialogOpen] = useState(false);
  // quickAddDialogOpen entfernt - Funktion in Erfassen-Dialog integriert
  const [writeOffDialogOpen, setWriteOffDialogOpen] = useState(false);
  const [settingsDialogOpen, setSettingsDialogOpen] = useState(false);
  const [editDialogOpen, setEditDialogOpen] = useState(false);
  const [scannerOpen, setScannerOpen] = useState(false);
  const [unknownGtinDialogOpen, setUnknownGtinDialogOpen] = useState(false);
  const [mhdSyncing, setMhdSyncing] = useState(false);
  const [bulkOpen, setBulkOpen] = useState(false);
  const [bulkPreview, setBulkPreview] = useState(null);
  const [bulkLoading, setBulkLoading] = useState(false);
  const [bulkRunning, setBulkRunning] = useState(false);
  
  // Form states
  const [newProduct, setNewProduct] = useState({
    ean: '',
    product_name: '',
    expiry_date: '',
    photo: null,
    image_url: '',
    location_id: '',
    quantity: '',  // P0.9 FIX: LEER, kein Default
    customer_image_path: ''  // Emergent Storage Pfad (nach Hintergrund-Upload gesetzt)
  });
  // Sofort-Abschreiben Modus im Erfassen-Dialog
  const [immediateWriteOffMode, setImmediateWriteOffMode] = useState(false);
  const [immediateWriteOffData, setImmediateWriteOffData] = useState({
    quantity: '1',
    article_number: '',
    noArticleNumber: false
  });
  const [selectedProduct, setSelectedProduct] = useState(null);
  const [editProduct, setEditProduct] = useState({
    ean: '',
    product_name: '',
    expiry_date: '',
    location_id: '',
    image_url: ''
  });
  const [editImageMode, setEditImageMode] = useState(null); // 'url' or 'photo'
  const [writeOffData, setWriteOffData] = useState({
    quantity: '',
    article_number: '',
    noArticleNumber: false,
    isSold: false
  });
  const [loadingProduct, setLoadingProduct] = useState(false);
  const [submitting, setSubmitting] = useState(false);
  // Dubletten-Warnung: null = kein Duplikat, object = Duplikat-Info vom Backend
  const [duplicateWarning, setDuplicateWarning] = useState(null);
  // Speichert die "Trotzdem speichern"-Aktion (welcher Flow das Duplikat auslöste)
  const [pendingForceAction, setPendingForceAction] = useState(null);
  const fileInputRef = useRef(null);
  const editFileInputRef = useRef(null);
  const lookupLockRef = useRef(false);  // Scan-Lock für EAN-Lookup

  // ── Android-Fix 1: Draft aus SessionStorage wiederherstellen (mount + visibilitychange)
  // Wenn Android die Tab-Seite bei Kamera-Nutzung neu lädt, landen wir hier mit den gespeicherten Daten
  useEffect(() => {
    const restoreDraft = () => {
      try {
        if (sessionStorage.getItem(MHD_CAMERA_KEY) !== 'true') return;
        sessionStorage.removeItem(MHD_CAMERA_KEY);
        const raw = sessionStorage.getItem(MHD_DRAFT_KEY);
        if (!raw) return;
        const draft = JSON.parse(raw);
        if (draft.ean || draft.product_name) {
          setNewProduct(prev => ({ ...prev, ...draft }));
          setAddDialogOpen(true);
          if (draft.unknownGtinOpen) setUnknownGtinDialogOpen(true);
        }
      } catch (e) {}
    };
    // Beim Laden der Seite
    restoreDraft();
    // Und wenn Tab wieder sichtbar wird (häufigerer Android-Fall: kein Reload, nur Vordergrund)
    document.addEventListener('visibilitychange', restoreDraft);
    return () => document.removeEventListener('visibilitychange', restoreDraft);
  }, []); // nur beim Mount

  // ── Android-Fix 2: Scanner-Back-Button abfangen (popstate → Scanner schließen statt navigieren)
  useEffect(() => {
    if (!scannerOpen) return;
    window.history.pushState({ mhd_scanner: true }, '');
    const handlePop = () => setScannerOpen(false);
    window.addEventListener('popstate', handlePop);
    return () => window.removeEventListener('popstate', handlePop);
  }, [scannerOpen]);

  // Wait for auth to be ready before fetching data.
  // This prevents a race condition on page reload where fetchData fires
  // before AuthContext sets the axios Authorization header, causing a 401 + logout.
  useEffect(() => {
    if (!authLoading) {
      setCurrentPage(1);
      fetchData(1);
    }
  }, [selectedLocation, authLoading]); // authLoading ensures we wait for auth header to be set

  // Reset beim Schließen des Add-Dialogs
  useEffect(() => {
    if (!addDialogOpen) {
      setScannerOpen(false);
      setImmediateWriteOffMode(false);
      setImmediateWriteOffData({ quantity: '1', article_number: '', noArticleNumber: false });
    }
  }, [addDialogOpen]);

  const fetchData = async (page = 1) => {
    setLoading(true);
    try {
      const locationParam = selectedLocation !== 'all' ? `&location_id=${selectedLocation}` : '';
      const [productsRes, locationsRes, settingsRes, writeOffsRes] = await Promise.all([
        axios.get(`${API}/mhd/products?page=${page}&page_size=${PAGE_SIZE}${locationParam}`),
        axios.get(`${API}/user/locations`),
        axios.get(`${API}/mhd/settings`),
        isAdmin ? axios.get(`${API}/mhd/writeoffs${selectedLocation !== 'all' ? `?location_id=${selectedLocation}` : ''}`) : Promise.resolve({ data: [] })
      ]);
      // Handle paginated response {items, total, page, page_size, total_pages}
      const data = productsRes.data;
      if (data && Array.isArray(data.items)) {
        setProducts(data.items);
        setTotalProducts(data.total || 0);
        setTotalPages(data.total_pages || 1);
        setCurrentPage(data.page || 1);
      } else {
        // Fallback for non-paginated legacy responses
        setProducts(Array.isArray(data) ? data : []);
      }
      setLocations(locationsRes.data);
      setSettings(settingsRes.data);
      setWriteOffs(writeOffsRes.data || []);
      
      if (locationsRes.data.length > 0 && !newProduct.location_id) {
        setNewProduct(prev => ({ ...prev, location_id: locationsRes.data[0].id }));
      }
    } catch (error) {
      console.error('Failed to fetch data:', error);
      toast.error('Fehler beim Laden');
    } finally {
      setLoading(false);
    }
  };

  const lookupProduct = async (ean) => {
    if (!ean) {
      toast.error('Bitte EAN eingeben');
      return;
    }
    
    // EAN-Validierung (nur numerisch, Länge 8/12/13)
    const trimmedEan = ean.trim();
    const isValidEAN = /^\d{8}$|^\d{12}$|^\d{13}$/.test(trimmedEan);
    
    if (!isValidEAN) {
      toast.error('Ungültige EAN (nur 8, 12 oder 13 Ziffern erlaubt)');
      return;
    }
    
    // Scan-Lock (verhindert Doppel-Lookups während API-Call)
    if (lookupLockRef.current) {
      return;
    }
    
    lookupLockRef.current = true;
    setLoadingProduct(true);
    
    try {
      // 🚀 PHASE A: Lokale DB (SOFORT, <300ms)
      const localResponse = await axios.get(`${API}/mhd/lookup-product-local/${trimmedEan}`);
      
      if (localResponse.data.found) {
        // ✅ Lokal gefunden - fertig!
        setNewProduct(prev => ({
          ...prev,
          ean: trimmedEan,
          product_name: localResponse.data.product_name || prev.product_name,
          image_url: localResponse.data.image_url || ''
        }));
        toast.success(`Produkt gefunden: ${localResponse.data.product_name}`);
        setLoadingProduct(false);
        lookupLockRef.current = false;
        // Art.-Nr. vorausfüllen (fire-and-forget)
        axios.get(`${API}/api/article-nr/lookup`, {
          headers: { Authorization: `Bearer ${localStorage.getItem('token')}` },
          params: { ean: trimmedEan }
        }).then(r => {
          if (r.data?.article_number)
            setImmediateWriteOffData(prev => ({ ...prev, article_number: r.data.article_number, _artNrManual: false }));
        }).catch(() => {});
        return;
      }
      
      // 🌐 PHASE B: OpenFoodFacts (ASYNC, max 2.5s)
      // Starte im Hintergrund, aber warte max 2.5s
      const offPromise = axios.get(`${API}/mhd/lookup-product-off/${trimmedEan}`);
      
      // Timeout nach 2.5s
      const timeoutPromise = new Promise((resolve) => 
        setTimeout(() => resolve({ data: { found: false, timeout: true } }), 2500)
      );
      
      const offResponse = await Promise.race([offPromise, timeoutPromise]);
      
      if (offResponse.data.found && !offResponse.data.timeout) {
        // ✅ OpenFoodFacts gefunden
        setNewProduct(prev => ({
          ...prev,
          ean: trimmedEan,
          product_name: offResponse.data.product_name || prev.product_name,
          image_url: offResponse.data.image_url || ''
        }));
        toast.success(`Produkt gefunden: ${offResponse.data.product_name}`);
        // Art.-Nr. vorausfüllen (fire-and-forget)
        axios.get(`${API}/api/article-nr/lookup`, {
          headers: { Authorization: `Bearer ${localStorage.getItem('token')}` },
          params: { ean: trimmedEan }
        }).then(r => {
          if (r.data?.article_number)
            setImmediateWriteOffData(prev => ({ ...prev, article_number: r.data.article_number, _artNrManual: false }));
        }).catch(() => {});
      } else {
        // ❌ Nicht gefunden oder Timeout
        setNewProduct(prev => ({ ...prev, ean: trimmedEan }));
        setUnknownGtinDialogOpen(true);
        
        if (offResponse.data.timeout) {
          toast.info('Lokale Suche ohne Ergebnis (max 2.5s erreicht)');
        }
      }
    } catch (error) {
      console.error('Product lookup failed:', error);
      setNewProduct(prev => ({ ...prev, ean: trimmedEan }));
      setUnknownGtinDialogOpen(true);
    } finally {
      setLoadingProduct(false);
      lookupLockRef.current = false;
    }
  };

  const handleBarcodeDetected = (ean) => {
    setScannerOpen(false);
    setNewProduct(prev => ({ ...prev, ean: ean }));
    lookupProduct(ean);
  };

  // ── Android-Fix 3: Kamera-Klick mit Draft-Sicherung
  // Speichert den aktuellen Formular-Zustand in SessionStorage VOR dem Öffnen der Kamera-App.
  const handleCameraClick = useCallback(() => {
    try {
      sessionStorage.setItem(MHD_DRAFT_KEY, JSON.stringify({
        ean: newProduct.ean,
        product_name: newProduct.product_name,
        expiry_date: newProduct.expiry_date,
        location_id: newProduct.location_id,
        quantity: newProduct.quantity,
        unknownGtinOpen: unknownGtinDialogOpen,
      }));
      sessionStorage.setItem(MHD_CAMERA_KEY, 'true');
    } catch (e) {}
    fileInputRef.current?.click();
  }, [newProduct, unknownGtinDialogOpen]);

  const handlePhotoCapture = async (event) => {
    // Einfacher Foto-Upload (ohne KI)
    const file = event.target.files?.[0];
    if (!file) return;
    
    // Originaldatei für Storage-Upload sichern (before input reset)
    const capturedFile = file;
    
    const reader = new FileReader();
    reader.onloadend = async () => {
      // Android-Fix: Bild komprimieren (max 800px, JPEG 0.72) bevor es gespeichert wird
      // → verhindert Timeouts bei großen Kamera-Bildern (z. B. 12MP = ~4MB base64 → ~200KB)
      const compressed = await compressImage(reader.result);
      setNewProduct(prev => ({ 
        ...prev, 
        photo: compressed,
        image_url: compressed
      }));
      // Draft-Key löschen, Foto erfolgreich
      try { sessionStorage.removeItem(MHD_CAMERA_KEY); } catch (e) {}
      toast.success('Foto übernommen');
      
      // 🔄 Hintergrund-Upload zu Emergent Storage (non-blocking)
      // Nur wenn EAN bekannt – ansonsten wird beim Speichern kein Storage-Pfad gesetzt
      setNewProduct(prev => {
        if (prev.ean) {
          uploadPhotoToStorage(capturedFile, prev.ean, prev.location_id || locations[0]?.id);
        }
        return prev;
      });
    };
    reader.readAsDataURL(capturedFile);
    
    if (fileInputRef.current) {
      fileInputRef.current.value = '';
    }
  };
  
  // Lädt Foto zu Emergent Storage hoch (im Hintergrund, non-blocking)
  const uploadPhotoToStorage = useCallback(async (file, ean, location_id) => {
    if (!file || !ean) return;
    const token = localStorage.getItem('token') || sessionStorage.getItem('token');
    const formData = new FormData();
    formData.append('file', file);
    formData.append('ean', ean);
    formData.append('location_id', location_id || 'default');
    try {
      const resp = await axios.post(`${API}/products/upload-image`, formData, {
        headers: { 'Content-Type': 'multipart/form-data', Authorization: `Bearer ${token}` }
      });
      setNewProduct(prev => ({ ...prev, customer_image_path: resp.data.path }));
    } catch (e) {
      // Nicht-blocking: bei Fehler wird base64 als Fallback verwendet
      console.warn('[Storage] Foto-Upload fehlgeschlagen, base64 als Fallback:', e?.response?.data?.detail || e.message);
    }
  }, [locations]);

  const handleSubmitProduct = async (force = false) => {
    // Wenn im Sofort-Abschreiben-Modus, andere Validierung
    if (immediateWriteOffMode) {
      if (!newProduct.ean || !newProduct.product_name) {
        toast.error('Bitte EAN und Produktname eingeben');
        return;
      }
      if (!immediateWriteOffData.quantity) {
        toast.error('Bitte Stückzahl eingeben');
        return;
      }
      if (!immediateWriteOffData.noArticleNumber && !immediateWriteOffData.article_number) {
        toast.error('Bitte Artikelnummer eingeben oder "nicht vorhanden" ankreuzen');
        return;
      }
    } else {
      if (!newProduct.ean || !newProduct.product_name || !newProduct.expiry_date || !newProduct.location_id) {
        toast.error('Bitte alle Pflichtfelder ausfüllen');
        return;
      }
    }

    setSubmitting(true);
    try {
      // P0: Rollenbasiert - Superadmin direkt, andere ggf. auto-aktiviert aus Master
      const saveResp = await axios.post(`${API}/mhd/save-master-product`, {
        ean: newProduct.ean,
        product_name: newProduct.product_name,
        image_url: newProduct.image_url,
        image_data: newProduct.photo,
        location_id: newProduct.location_id || locations[0]?.id,
        source: 'manual'
      });
      // Wenn Produkt direkt aus Master aktiviert wurde, Produktnamen aus Master übernehmen
      const activatedFromMaster = saveResp.data?.status === 'activated' || saveResp.data?.status === 'already_active';
      if (activatedFromMaster && saveResp.data?.mapping?.product_name) {
        setNewProduct(prev => ({ ...prev, product_name: saveResp.data.mapping.product_name }));
      }
      
      if (immediateWriteOffMode) {
        // Sofort abschreiben - direkt writeoff ohne MHD-Tracking
        await axios.post(`${API}/mhd/immediate-writeoff`, {
          ean: newProduct.ean,
          product_name: newProduct.product_name,
          image_url: newProduct.image_url || newProduct.photo || '',
          quantity: parseInt(immediateWriteOffData.quantity),
          article_number: immediateWriteOffData.noArticleNumber ? '' : (immediateWriteOffData.article_number?.trim() || ''),
          location_id: newProduct.location_id || locations[0]?.id,
          force: force,  // FIX B: Duplikat-Override weitergeben
        });
        toast.success('Produkt sofort abgeschrieben');
      } else {
        // Normaler MHD-Eintrag mit quantity (optional)
        // ?force=true überspringt den Dubletten-Check
        await axios.post(`${API}/mhd/products?force=${force}`, {
          ean: newProduct.ean,
          product_name: newProduct.product_name,
          expiry_date: newProduct.expiry_date,
          photo: newProduct.customer_image_path ? null : newProduct.photo, // base64 nur wenn kein Storage-Pfad
          image_url: newProduct.customer_image_path ? null : newProduct.image_url,
          customer_image_path: newProduct.customer_image_path || null,
          location_id: newProduct.location_id,
          quantity: newProduct.quantity || null,  // P0.9 FIX: null wenn leer
          article_number: newProduct.article_number?.trim() || null  // JET-Art.Nr. am MHD-Produkt speichern
        });
        toast.success('Produkt erfasst');

        // Artikelnummer für diese EAN merken (falls eingegeben)
        if (newProduct.article_number?.trim() && newProduct.ean) {
          axios.post(`${API}/api/article-nr/save`, {
            ean: newProduct.ean,
            article_number: newProduct.article_number.trim(),
            product_name: newProduct.product_name,
            location_id: newProduct.location_id || null,
          }, { headers: { Authorization: `Bearer ${localStorage.getItem('token')}` } })
          .catch(() => {});
        }
      }
      
      setDuplicateWarning(null);
      setAddDialogOpen(false);
      setNewProduct({
        ean: '',
        product_name: '',
        expiry_date: '',
        photo: null,
        image_url: '',
        location_id: locations[0]?.id || '',
        quantity: '',  // P0.9 FIX: Reset zu leer
        customer_image_path: ''
      });
      setImmediateWriteOffMode(false);
      setImmediateWriteOffData({ quantity: '1', article_number: '', noArticleNumber: false });
      fetchData(currentPage);
    } catch (error) {
      // Dubletten-Warnung: 409 → Modal anzeigen statt Error-Toast
      // Gilt für DUPLICATE_PRODUCT (Produkt erfassen) UND DUPLICATE_WRITEOFF (sofort abschreiben)
      if (error.response?.status === 409 &&
          ['DUPLICATE_PRODUCT', 'DUPLICATE_WRITEOFF'].includes(error.response?.data?.detail?.code)) {
        setDuplicateWarning(error.response.data.detail);
        setPendingForceAction(() => () => handleSubmitProduct(true));
        return;
      }
      console.error('Failed to add product:', error);
      const errorMessage = error.response?.data?.detail || 
        (immediateWriteOffMode ? 'Fehler beim Abschreiben' : 'Fehler beim Speichern');
      toast.error(typeof errorMessage === 'string' ? errorMessage : 'Fehler beim Speichern');
    } finally {
      setSubmitting(false);
    }
  };

  // handleQuickAdd entfernt - Funktion in handleSubmitProduct integriert

  // unknownGTIN-Dialog: Speichern mit optionalem force-Parameter (Dubletten-Check)
  const handleSaveUnknownGtin = async (force = false) => {
    if (!newProduct.product_name.trim()) {
      toast.error('Bitte Produktname eingeben');
      return;
    }

    setSubmitting(true);
    try {
      // 1. Pending Product speichern (mit oder ohne Bild)
      await axios.post(`${API}/mhd/add-pending-product`, {
        ean: newProduct.ean,
        product_name: newProduct.product_name,
        location_id: newProduct.location_id || locations[0]?.id,
        image_url: newProduct.image_url || '',
        local_image_data: newProduct.photo || ''
      });

      // 2. Wenn Ablaufdatum gesetzt, MHD-Eintrag erstellen (mit Dubletten-Check)
      if (newProduct.expiry_date) {
        await axios.post(`${API}/mhd/products?force=${force}`, {
          ean: newProduct.ean,
          product_name: newProduct.product_name,
          expiry_date: newProduct.expiry_date,
          photo: newProduct.photo,
          image_url: newProduct.image_url,
          location_id: newProduct.location_id || locations[0]?.id,
          quantity: newProduct.quantity || null
        });

        // Mit Ablaufdatum: Komplett fertig
        toast.success('Produkt erfasst und wartet auf Freigabe');
        setDuplicateWarning(null);
        // BEIDE Dialogs schließen + Form resetten
        setUnknownGtinDialogOpen(false);
        setAddDialogOpen(false);
        setNewProduct({
          ean: '',
          product_name: '',
          expiry_date: '',
          photo: null,
          image_url: '',
          location_id: locations[0]?.id || '',
          quantity: ''
        });
        fetchData(currentPage);
      } else {
        // Ohne Ablaufdatum: Nur unknown Dialog schließen, zurück zum Hauptdialog
        toast.success('Produkt angelegt. Bitte Ablaufdatum eingeben.');
        setUnknownGtinDialogOpen(false);
        // addDialogOpen bleibt OFFEN!
      }
    } catch (error) {
      // Dubletten-Warnung: 409 → Modal anzeigen statt Error-Toast
      if (error.response?.status === 409 && error.response?.data?.detail?.code === 'DUPLICATE_PRODUCT') {
        setDuplicateWarning(error.response.data.detail);
        setPendingForceAction(() => () => handleSaveUnknownGtin(true));
        return;
      }
      console.error('Failed to add pending product:', error);
      toast.error(error.response?.data?.detail || 'Fehler beim Speichern');
    } finally {
      setSubmitting(false);
    }
  };


  const handleWriteOff = async () => {
    // Bei Abverkauft keine Validierung für Stückzahl
    if (!writeOffData.isSold) {
      // Check if article_number is required
      if (!writeOffData.quantity) {
        toast.error('Bitte Stückzahl eingeben');
        return;
      }
      
      // BUGFIX: Article number is required ONLY if checkbox is NOT checked
      // If checkbox IS checked (noArticleNumber = true), article_number is NOT required
      if (!writeOffData.noArticleNumber) {
        // Checkbox is NOT checked → article_number IS required
        if (!writeOffData.article_number || writeOffData.article_number.trim() === '') {
          toast.error('Artikelnummer erforderlich. Bitte eingeben oder "nicht vorhanden" ankreuzen');
          return;
        }
      }
      // If checkbox IS checked (noArticleNumber = true), skip validation → allow empty article_number
    }

    setSubmitting(true);
    try {
      await axios.post(`${API}/mhd/writeoff`, {
        product_id: selectedProduct.id,
        quantity: writeOffData.isSold ? null : parseInt(writeOffData.quantity),
        // Bei Abverkauft: Artikelnummer wird ebenfalls gespeichert (echte Nr., kein EAN-Fallback → Option a)
        article_number: writeOffData.noArticleNumber ? '' : (writeOffData.article_number?.trim() || ''),
        noArticleNumber: writeOffData.noArticleNumber,  // BUGFIX: Send flag to backend
        removal_type: writeOffData.isSold ? 'sold' : 'written_off'
      });
      toast.success(writeOffData.isSold ? 'Produkt als abverkauft markiert' : 'Produkt abgeschrieben');

      // Art.-Nr. speichern wenn vorhanden (fire-and-forget)
      if (!writeOffData.noArticleNumber && writeOffData.article_number?.trim() && selectedProduct.ean) {
        const locId = selectedLocation !== 'all' ? selectedLocation : (selectedProduct?.location_id || null);
        axios.post(`${API}/api/article-nr/save`, {
          ean: selectedProduct.ean,
          article_number: writeOffData.article_number.trim(),
          product_name: selectedProduct.product_name || null,
          location_id: locId || null,
        }, { headers: { Authorization: `Bearer ${localStorage.getItem('token')}` } }).catch(() => {});
      }
      setWriteOffDialogOpen(false);
      setSelectedProduct(null);
      setWriteOffData({ quantity: '', article_number: '', noArticleNumber: false, isSold: false, _artNrManual: false });
      fetchData(currentPage);
    } catch (error) {
      console.error('Failed to write off product:', error);
      const errorMessage = error.response?.data?.detail || 
        (writeOffData.isSold ? 'Fehler beim Markieren' : 'Fehler beim Abschreiben');
      toast.error(errorMessage);
    } finally {
      setSubmitting(false);
    }
  };

  const handleSaveSettings = async () => {
    try {
      await axios.put(`${API}/mhd/settings`, { warning_days: settings.warning_days });
      toast.success('Einstellungen gespeichert');
      setSettingsDialogOpen(false);
      fetchData(currentPage);
    } catch (error) {
      toast.error('Fehler beim Speichern');
    }
  };

  const openBulkCleanup = async () => {
    setBulkLoading(true);
    setBulkOpen(true);
    setBulkPreview(null);
    try {
      const token = localStorage.getItem('token') || sessionStorage.getItem('token');
      const locParam = selectedLocation !== 'all' ? `&location_id=${selectedLocation}` : '';
      const r = await axios.get(`${API}/mhd/bulk-writeoff/preview?only_expired=true${locParam}`, {
        headers: { Authorization: `Bearer ${token}` },
      });
      setBulkPreview(r.data);
    } catch (e) {
      toast.error('Vorschau fehlgeschlagen', { description: e.response?.data?.detail || '' });
      setBulkOpen(false);
    } finally {
      setBulkLoading(false);
    }
  };

  const runBulkCleanup = async () => {
    setBulkRunning(true);
    try {
      const token = localStorage.getItem('token') || sessionStorage.getItem('token');
      const body = { only_expired: true, confirm: true };
      if (selectedLocation !== 'all') body.location_id = selectedLocation;
      const r = await axios.post(`${API}/mhd/bulk-writeoff`, body, {
        headers: { Authorization: `Bearer ${token}` },
      });
      toast.success(`${r.data.written_off} Artikel abgeschrieben`, {
        description: `Gesamtmenge ${r.data.total_quantity}${r.data.errors ? ` · ${r.data.errors} Fehler` : ''}`,
      });
      setBulkOpen(false);
      window.location.reload();
    } catch (e) {
      toast.error('Bereinigung fehlgeschlagen', { description: e.response?.data?.detail || '' });
    } finally {
      setBulkRunning(false);
    }
  };

  const handleExportWriteOffs = async () => {
    try {
      const response = await axios.get(
        `${API}/mhd/writeoffs/export-pdf${selectedLocation !== 'all' ? `?location_id=${selectedLocation}` : ''}`,
        { responseType: 'blob' }
      );
      
      const blob = new Blob([response.data], { type: 'application/pdf' });
      const url = window.URL.createObjectURL(blob);
      const link = document.createElement('a');
      link.href = url;
      link.download = `MHD-Abschriften_${new Date().toISOString().split('T')[0]}.pdf`;
      document.body.appendChild(link);
      link.click();
      document.body.removeChild(link);
      window.URL.revokeObjectURL(url);
      
      toast.success('PDF exportiert');
    } catch (error) {
      toast.error('Export fehlgeschlagen');
    }
  };

  // ── WWS-Status Handler ──────────────────────────────────────────────────
  const handleWwsToggle = useCallback(async (writeoffId, abgeschrieben) => {
    setWwsLoading(true);
    try {
      await axios.patch(`${API}/mhd/writeoffs/${writeoffId}/wws-status`, { abgeschrieben });
      setWriteOffs(prev => prev.map(w =>
        w.id === writeoffId ? { ...w, wws_abgeschrieben: abgeschrieben } : w
      ));
      toast.success(abgeschrieben ? 'Im WWS abgeschrieben markiert' : 'Status zurückgesetzt');
    } catch {
      toast.error('Fehler beim Aktualisieren');
    } finally {
      setWwsLoading(false);
    }
  }, []);

  const handleBulkWwsStatus = useCallback(async (abgeschrieben, allFiltered = false) => {
    const ids = allFiltered ? [] : Array.from(selectedWriteoffs);
    if (!allFiltered && ids.length === 0) {
      toast.error('Keine Einträge ausgewählt');
      return;
    }
    setWwsLoading(true);
    try {
      const payload = allFiltered
        ? { all_filtered: true, location_id: selectedLocation !== 'all' ? selectedLocation : null, abgeschrieben }
        : { writeoff_ids: ids, abgeschrieben };
      const res = await axios.patch(`${API}/mhd/writeoffs/bulk-wws-status`, payload);
      // Refresh data
      await fetchData(currentPage);
      setSelectedWriteoffs(new Set());
      toast.success(`${res.data.updated} Einträge ${abgeschrieben ? 'als Im WWS markiert' : 'zurückgesetzt'}`);
    } catch {
      toast.error('Fehler beim Bulk-Update');
    } finally {
      setWwsLoading(false);
    }
  }, [selectedWriteoffs, selectedLocation, currentPage]);

  const toggleWriteoffSelection = useCallback((id) => {
    setSelectedWriteoffs(prev => {
      const next = new Set(prev);
      if (next.has(id)) next.delete(id);
      else next.add(id);
      return next;
    });
  }, []);

  const toggleSelectAll = useCallback(() => {
    if (selectedWriteoffs.size === writeOffs.length) {
      setSelectedWriteoffs(new Set());
    } else {
      setSelectedWriteoffs(new Set(writeOffs.map(w => w.id)));
    }
  }, [writeOffs, selectedWriteoffs]);

  const openWriteOffDialog = useCallback(async (product) => {
    setSelectedProduct(product);
    // Sofort-Anzeige: am MHD-Produkt gespeicherte Art.-Nr. (Lieferung/direkte Erfassung) → kein Flackern
    const initialArt = (product?.article_number || '').trim();
    setWriteOffData({ quantity: '', article_number: initialArt, noArticleNumber: false, isSold: false, _artNrManual: false });
    setWriteOffDialogOpen(true);

    // Hintergrund-Merker hat Vorrang: enthält die ZULETZT gelernte/manuell korrigierte Nr.
    const ean = product?.ean;
    if (ean) {
      const locId = selectedLocation !== 'all' ? selectedLocation : (product?.location_id || '');
      const params = new URLSearchParams({ ean });
      if (locId) params.set('location_id', locId);
      try {
        const r = await fetch(`${API}/api/article-nr/lookup?${params}`, {
          headers: { Authorization: `Bearer ${localStorage.getItem('token')}` },
        });
        const d = await r.json();
        // Nur überschreiben wenn der User noch nichts manuell getippt hat
        if (d.article_number) {
          setWriteOffData(prev => (prev._artNrManual ? prev : { ...prev, article_number: d.article_number, _artNrManual: false }));
        }
      } catch {}
    }
  }, [selectedLocation]);

  // Memoized categorized products - WICHTIGE PERFORMANCE-OPTIMIERUNG
  const { expiredProducts, todayProducts, soonProducts, okProducts, todayStr, warningDateStr } = useMemo(() => {
    const today = new Date();
    today.setHours(0, 0, 0, 0);
    const todayStr = today.toISOString().split('T')[0];

    // Morgen (Produkte mit 1 Tag Restzeit müssen HEUTE gehandelt werden)
    const tomorrow = new Date(today);
    tomorrow.setDate(tomorrow.getDate() + 1);
    const tomorrowStr = tomorrow.toISOString().split('T')[0];

    const warningDate = new Date(today);
    warningDate.setDate(warningDate.getDate() + settings.warning_days);
    const warningDateStr = warningDate.toISOString().split('T')[0];

    return {
      expiredProducts: products.filter(p => p.expiry_date < todayStr),
      // "Läuft heute ab" = heute (0 Tage) ODER morgen (1 Tag) → sofortiger Handlungsbedarf
      todayProducts: products.filter(p => p.expiry_date === todayStr || p.expiry_date === tomorrowStr),
      // "Bald ablaufend" = übermorgen bis Warnung-Datum (2+ Tage)
      soonProducts: products.filter(p => p.expiry_date > tomorrowStr && p.expiry_date <= warningDateStr),
      okProducts: products.filter(p => p.expiry_date > warningDateStr),
      todayStr,
      warningDateStr
    };
  }, [products, settings.warning_days]);

  // Edit product functions
  const openEditDialog = useCallback(async (product) => {
    setSelectedProduct(product);
    setEditImageMode(null);
    // Set basic data immediately so dialog opens fast
    setEditProduct({
      ean: product.ean || '',
      product_name: product.product_name || '',
      expiry_date: product.expiry_date || '',
      location_id: product.location_id || '',
      image_url: product.image_url || ''
    });
    setEditDialogOpen(true);
    
    // If product has a local photo (base64 stripped from list for perf),
    // fetch the full product detail to show the photo in the edit dialog
    if (product.has_local_photo && !product.image_url) {
      try {
        const response = await axios.get(`${API}/mhd/products/${product.id}`);
        const full = response.data;
        setEditProduct(prev => ({
          ...prev,
          image_url: full.image_url || full.photo || prev.image_url
        }));
      } catch (err) {
        console.warn('Could not load full product photo:', err);
      }
    }
  }, []);

  const handleEditProduct = async () => {
    if (!editProduct.product_name || !editProduct.expiry_date) {
      toast.error('Bitte Produktname und Ablaufdatum eingeben');
      return;
    }

    setSubmitting(true);
    try {
      await axios.put(`${API}/mhd/products/${selectedProduct.id}`, editProduct);
      toast.success('Produkt aktualisiert');
      setEditDialogOpen(false);
      setSelectedProduct(null);
      setEditImageMode(null);
      fetchData(currentPage);
    } catch (error) {
      console.error('Failed to update product:', error);
      toast.error('Fehler beim Aktualisieren');
    } finally {
      setSubmitting(false);
    }
  };

  const handleEditPhotoCapture = (event) => {
    const file = event.target.files?.[0];
    if (!file) return;
    
    const reader = new FileReader();
    reader.onloadend = () => {
      setEditProduct(prev => ({ ...prev, image_url: reader.result }));
      setEditImageMode(null);
      toast.success('Foto übernommen');
    };
    reader.readAsDataURL(file);
    
    if (editFileInputRef.current) {
      editFileInputRef.current.value = '';
    }
  };

  if (loading) {
    return (
      <DashboardLayout>
        <div className="flex items-center justify-center h-96">
          <Loader2 className="h-12 w-12 animate-spin text-primary" />
        </div>
      </DashboardLayout>
    );
  }

  return (
    <>
    <DashboardLayout>
      <div className="space-y-4">
        {/* Header */}
        <div className="flex flex-col sm:flex-row sm:items-center justify-between gap-3">
          <div>
            <h1 className="text-2xl font-bold" style={{ fontFamily: 'Manrope, sans-serif' }}>
              MHD
            </h1>
            <p className="text-muted-foreground text-sm">
              {totalProducts > 0 ? `${totalProducts} Produkte insgesamt` : `${products.length} Produkte erfasst`}
              {totalPages > 1 && ` · Seite ${currentPage}/${totalPages}`}
            </p>
          </div>
          <div className="flex items-center gap-2">
            {isAdmin && (
              <>
                <Button variant="outline" size="sm" onClick={() => navigate('/admin/mhd-export')}>
                  <Download className="h-4 w-4 mr-1" />
                  Export
                </Button>
                <Button variant="outline" size="sm" onClick={() => setSettingsDialogOpen(true)}>
                  <Settings className="h-4 w-4" />
                </Button>
                <Button
                  variant="outline"
                  size="sm"
                  disabled={mhdSyncing}
                  data-testid="sync-mhd-btn"
                  onClick={async () => {
                    setMhdSyncing(true);
                    try {
                      const token = localStorage.getItem('token') || sessionStorage.getItem('token');
                      const r = await axios.post(`${API}/lieferung/sync-mhd`, {}, {
                        headers: { Authorization: `Bearer ${token}` },
                      });
                      const { created, skipped, message } = r.data;
                      if (created > 0) {
                        toast.success(`MHD-Synchronisation abgeschlossen`, { description: message });
                        // Liste neu laden
                        window.location.reload();
                      } else {
                        toast.info('Alles synchron', { description: message });
                      }
                    } catch {
                      toast.error('Synchronisation fehlgeschlagen');
                    } finally {
                      setMhdSyncing(false);
                    }
                  }}
                >
                  {mhdSyncing ? <Loader2 className="h-4 w-4 animate-spin" /> : <RefreshCw className="h-4 w-4" />}
                  <span className="ml-1 hidden sm:inline">Sync</span>
                </Button>
                <Button
                  variant="outline"
                  size="sm"
                  className="text-red-600 border-red-200 hover:bg-red-50 dark:hover:bg-red-950/30"
                  data-testid="bulk-cleanup-btn"
                  onClick={openBulkCleanup}
                  title="Alle abgelaufenen Artikel auf einmal abschreiben"
                >
                  <Trash2 className="h-4 w-4" />
                  <span className="ml-1 hidden sm:inline">Rückstand bereinigen</span>
                </Button>
              </>
            )}
            <Button onClick={() => setAddDialogOpen(true)} size="sm">
              <Plus className="h-4 w-4 mr-1" />
              Erfassen
            </Button>
          </div>
        </div>

        {/* Location Filter */}
        {isAdmin && locations.length > 1 && (
          <Select value={selectedLocation} onValueChange={setSelectedLocation}>
            <SelectTrigger className="w-full sm:w-[250px]">
              <SelectValue placeholder="Alle Standorte" />
            </SelectTrigger>
            <SelectContent>
              <SelectItem value="all">Alle Standorte</SelectItem>
              {locations.map(loc => (
                <SelectItem key={loc.id} value={loc.id}>{loc.name}</SelectItem>
              ))}
            </SelectContent>
          </Select>
        )}

        {/* Summary Stats */}
        <div className="grid grid-cols-4 gap-2">
          <Card className="border-red-200 dark:border-red-800 bg-red-50 dark:bg-red-950/40">
            <CardContent className="py-2 text-center">
              <div className="text-xl font-bold text-red-600 dark:text-red-400">{expiredProducts.length}</div>
              <p className="text-xs text-red-700 dark:text-red-400">Abgelaufen</p>
            </CardContent>
          </Card>
          <Card className="border-orange-200 dark:border-orange-800 bg-orange-50 dark:bg-orange-950/40">
            <CardContent className="py-2 text-center">
              <div className="text-xl font-bold text-orange-600 dark:text-orange-400">{todayProducts.length}</div>
              <p className="text-xs text-orange-700 dark:text-orange-400">Läuft ab</p>
            </CardContent>
          </Card>
          <Card className="border-yellow-200 dark:border-yellow-800 bg-yellow-50 dark:bg-yellow-950/40">
            <CardContent className="py-2 text-center">
              <div className="text-xl font-bold text-yellow-600 dark:text-yellow-400">{soonProducts.length}</div>
              <p className="text-xs text-yellow-700 dark:text-yellow-400">Bald</p>
            </CardContent>
          </Card>
          <Card className="border-green-200 dark:border-green-800 bg-green-50 dark:bg-green-950/40">
            <CardContent className="py-2 text-center">
              <div className="text-xl font-bold text-green-600 dark:text-green-400">{okProducts.length}</div>
              <p className="text-xs text-green-700 dark:text-green-400">OK</p>
            </CardContent>
          </Card>
        </div>

        {/* Collapsible Product Lists */}
        <div className="space-y-3">
          {/* Expired (Abgelaufen - nicht abgeschrieben) */}
          {expiredProducts.length > 0 && (
            <CategorySection
              title="Abgelaufen"
              icon={AlertCircle}
              count={expiredProducts.length}
              color="text-red-600"
              bgColor="bg-red-50 dark:bg-red-950/40 border-red-200 dark:border-red-800"
              defaultOpen={true}
            >
              {expiredProducts.map(product => (
                <ProductCard 
                  key={product.id} 
                  product={product} 
                  status="expired"
                  onWriteOff={openWriteOffDialog}
                  onEdit={openEditDialog}
                />
              ))}
            </CategorySection>
          )}

          {/* Läuft heute ab (= heute + morgen, sofortiger Handlungsbedarf) */}
          {todayProducts.length > 0 && (
            <CategorySection
              title="Läuft heute ab"
              icon={AlertTriangle}
              count={todayProducts.length}
              color="text-orange-600 dark:text-orange-400"
              bgColor="bg-orange-50 dark:bg-orange-950/40 border-orange-300 dark:border-orange-700"
              defaultOpen={true}
            >
              {todayProducts.map(product => (
                <ProductCard 
                  key={product.id} 
                  product={product} 
                  status="today"
                  onWriteOff={openWriteOffDialog}
                  onEdit={openEditDialog}
                />
              ))}
            </CategorySection>
          )}

          {/* Soon (Bald ablaufend) */}
          {soonProducts.length > 0 && (
            <CategorySection
              title="Bald ablaufend"
              icon={Clock}
              count={soonProducts.length}
              color="text-yellow-600"
              bgColor="bg-yellow-50 dark:bg-yellow-950/40 border-yellow-200 dark:border-yellow-800"
              defaultOpen={false}
            >
              {soonProducts.map(product => (
                <ProductCard 
                  key={product.id} 
                  product={product} 
                  status="soon"
                  onWriteOff={openWriteOffDialog}
                  onEdit={openEditDialog}
                />
              ))}
            </CategorySection>
          )}

          {/* OK (Noch haltbar) */}
          {okProducts.length > 0 && (
            <CategorySection
              title="Noch haltbar"
              icon={CheckCircle2}
              count={okProducts.length}
              color="text-green-600"
              bgColor="bg-green-50 dark:bg-green-950/40 border-green-200 dark:border-green-800"
              defaultOpen={false}
            >
              {okProducts.map(product => (
                <ProductCard 
                  key={product.id} 
                  product={product} 
                  status="ok"
                  onWriteOff={openWriteOffDialog}
                  onEdit={openEditDialog}
                />
              ))}
            </CategorySection>
          )}

          {/* Archived (Abgeschrieben) - Admin only */}
          {isAdmin && writeOffs.length > 0 && (
            <CategorySection
              title="Archiviert (Abgeschrieben)"
              icon={Archive}
              count={writeOffs.length}
              color="text-gray-600 dark:text-gray-400"
              bgColor="bg-gray-100 dark:bg-gray-800 border-gray-200 dark:border-gray-700"
              defaultOpen={false}
            >
              {/* Status-Zusammenfassung & Bulk-Toolbar */}
              {(() => {
                const wwsCount = writeOffs.filter(w => w.wws_abgeschrieben).length;
                const pendingCount = writeOffs.length - wwsCount;
                const allSelected = selectedWriteoffs.size === writeOffs.length && writeOffs.length > 0;
                const someSelected = selectedWriteoffs.size > 0;
                return (
                  <div className="mb-3 space-y-2">
                    {/* Status-Übersicht */}
                    <div className="flex flex-wrap items-center gap-2 text-xs">
                      {pendingCount > 0 && (
                        <span className="inline-flex items-center gap-1 px-2 py-1 rounded-full bg-orange-100 text-orange-700 dark:bg-orange-900/30 dark:text-orange-400 border border-orange-200 dark:border-orange-700 font-medium">
                          <Clock className="h-3 w-3" />
                          {pendingCount} noch nicht im WWS
                        </span>
                      )}
                      {wwsCount > 0 && (
                        <span className="inline-flex items-center gap-1 px-2 py-1 rounded-full bg-green-100 text-green-700 dark:bg-green-900/30 dark:text-green-400 border border-green-200 dark:border-green-700 font-medium">
                          <CheckCircle className="h-3 w-3" />
                          {wwsCount} Im WWS abgeschrieben
                        </span>
                      )}
                    </div>
                    {/* Aktions-Toolbar */}
                    <div className="flex flex-wrap items-center gap-2 p-2 rounded-lg bg-white dark:bg-gray-900 border border-gray-200 dark:border-gray-700">
                      {/* Alle auswählen */}
                      <label className="flex items-center gap-1.5 cursor-pointer text-xs text-gray-600 dark:text-gray-400 select-none">
                        <input
                          type="checkbox"
                          checked={allSelected}
                          onChange={toggleSelectAll}
                          className="h-3.5 w-3.5 accent-blue-600"
                          data-testid="wws-select-all"
                        />
                        Alle
                      </label>
                      <div className="w-px h-4 bg-gray-300 dark:bg-gray-600" />
                      {someSelected && (
                        <>
                          <span className="text-xs text-blue-600 dark:text-blue-400 font-medium">{selectedWriteoffs.size} ausgewählt</span>
                          <Button
                            size="sm"
                            variant="outline"
                            className="h-7 text-xs border-green-400 text-green-700 hover:bg-green-50 dark:border-green-700 dark:text-green-400"
                            disabled={wwsLoading}
                            onClick={() => handleBulkWwsStatus(true, false)}
                            data-testid="wws-bulk-mark-btn"
                          >
                            <CheckCircle className="h-3 w-3 mr-1" />
                            Im WWS markieren
                          </Button>
                          <Button
                            size="sm"
                            variant="ghost"
                            className="h-7 text-xs text-gray-500 hover:text-red-600"
                            disabled={wwsLoading}
                            onClick={() => handleBulkWwsStatus(false, false)}
                            data-testid="wws-bulk-reset-btn"
                          >
                            <X className="h-3 w-3 mr-1" />
                            Zurücksetzen
                          </Button>
                        </>
                      )}
                      {!someSelected && (
                        <Button
                          size="sm"
                          variant="outline"
                          className="h-7 text-xs border-green-400 text-green-700 hover:bg-green-50 dark:border-green-700 dark:text-green-400"
                          disabled={wwsLoading}
                          onClick={() => handleBulkWwsStatus(true, true)}
                          data-testid="wws-mark-all-btn"
                        >
                          <CheckCircle className="h-3 w-3 mr-1" />
                          Alle im WWS markieren
                        </Button>
                      )}
                      <div className="ml-auto">
                        <Button variant="outline" size="sm" className="h-7 text-xs" onClick={handleExportWriteOffs} data-testid="wws-pdf-export-btn">
                          <Download className="h-3 w-3 mr-1" />
                          PDF
                        </Button>
                      </div>
                    </div>
                  </div>
                );
              })()}
              {writeOffs.slice(0, 50).map(product => (
                <ArchivedProductCard
                  key={product.id}
                  product={product}
                  isSelected={selectedWriteoffs.has(product.id)}
                  onSelect={toggleWriteoffSelection}
                  onWwsToggle={handleWwsToggle}
                  wwsLoading={wwsLoading}
                />
              ))}
              {writeOffs.length > 50 && (
                <p className="text-sm text-muted-foreground text-center py-2">
                  + {writeOffs.length - 50} weitere...
                </p>
              )}
            </CategorySection>
          )}

          {/* Pagination Controls */}
          {totalPages > 1 && (
            <div className="flex items-center justify-center gap-3 py-4">
              <Button
                variant="outline" size="sm"
                onClick={() => { const p = currentPage - 1; setCurrentPage(p); fetchData(p); }}
                disabled={currentPage <= 1 || loading}
                data-testid="mhd-prev-page"
              >
                &larr; Zurück
              </Button>
              <span className="text-sm text-muted-foreground">
                Seite {currentPage} / {totalPages} ({totalProducts} Produkte)
              </span>
              <Button
                variant="outline" size="sm"
                onClick={() => { const p = currentPage + 1; setCurrentPage(p); fetchData(p); }}
                disabled={currentPage >= totalPages || loading}
                data-testid="mhd-next-page"
              >
                Weiter &rarr;
              </Button>
            </div>
          )}

          {/* Empty State */}
          {products.length === 0 && !loading && (
            <Card>
              <CardContent className="py-12 text-center">
                <Package className="h-12 w-12 mx-auto mb-3 text-gray-300 dark:text-gray-600" />
                <h3 className="font-semibold mb-1">Keine Produkte</h3>
                <p className="text-muted-foreground text-sm mb-4">
                  Scannen Sie einen Barcode zum Starten
                </p>
                <Button onClick={() => setAddDialogOpen(true)} size="sm">
                  <Plus className="h-4 w-4 mr-1" />
                  Produkt erfassen
                </Button>
              </CardContent>
            </Card>
          )}
        </div>
      </div>

      {/* Add Product Dialog */}
      <Dialog open={addDialogOpen} onOpenChange={(open) => {
        // Verhindere Schließen während des Speicherns
        if (!open && submitting) return;
        setAddDialogOpen(open);
      }}>
        <DialogContent className="sm:max-w-[450px]" style={{ WebkitOverflowScrolling: 'touch' }}>
          <DialogHeader>
            <DialogTitle className="flex items-center justify-between">
              <div className="flex items-center gap-2">
                <Package className="h-5 w-5 text-blue-600" />
                Produkt erfassen
              </div>
              {/* Reset Button */}
              {(newProduct.ean || newProduct.product_name || newProduct.photo || newProduct.image_url) && (
                <Button
                  variant="ghost"
                  size="sm"
                  onClick={() => {
                    setNewProduct({
                      ean: '',
                      product_name: '',
                      expiry_date: '',
                      photo: null,
                      image_url: '',
                      customer_image_path: '',
                      location_id: newProduct.location_id // Keep location
                    });
                    toast.info('Formular zurückgesetzt');
                  }}
                  className="text-orange-600 hover:text-orange-700 hover:bg-orange-50"
                >
                  <X className="h-4 w-4 mr-1" />
                  Reset
                </Button>
              )}
            </DialogTitle>
          </DialogHeader>

          {!scannerOpen && (
            <div className="space-y-4 py-2">
              {/* Hidden file input for photo */}
              <input
                type="file"
                accept="image/*"
                capture="environment"
                ref={fileInputRef}
                onChange={handlePhotoCapture}
                className="hidden"
              />
              
              {/* Scan Button */}
              <Button 
                variant="outline" 
                onClick={() => setScannerOpen(true)}
                className="w-full h-12 border-2 border-dashed border-blue-300 hover:border-blue-500 hover:bg-blue-50"
              >
                <Camera className="h-5 w-5 mr-2 text-blue-600" />
                <span className="font-medium">Barcode scannen</span>
              </Button>
              
              {/* Divider */}
              <div className="flex items-center gap-2">
                <div className="flex-1 border-t"></div>
                <span className="text-xs text-muted-foreground">oder manuell</span>
                <div className="flex-1 border-t"></div>
              </div>
              
              {/* Manual EAN */}
              <div className="space-y-1">
                <Label className="text-sm">EAN-Code *</Label>
                <div className="flex gap-2">
                  <Input
                    placeholder="EAN eingeben"
                    inputMode="numeric"
                    autoComplete="off"
                    type="text"
                    value={newProduct.ean}
                    onChange={(e) => setNewProduct(prev => ({ ...prev, ean: e.target.value }))}
                    onClick={(e) => e.currentTarget.focus()}
                    onKeyDown={(e) => {
                      if (e.key === 'Enter' && newProduct.ean.trim()) {
                        e.preventDefault();
                        lookupProduct(newProduct.ean.trim());
                      }
                    }}
                  />
                  <Button
                    variant="outline"
                    onClick={() => lookupProduct(newProduct.ean)}
                    disabled={!newProduct.ean || loadingProduct}
                  >
                    {loadingProduct ? <Loader2 className="h-4 w-4 animate-spin" /> : <Search className="h-4 w-4" />}
                  </Button>
                </div>
              </div>

              {/* Product Name */}
              <div className="space-y-1">
                <Label className="text-sm">Produktname *</Label>
                <ProductNameAutocomplete
                  value={newProduct.product_name}
                  onChange={(v) => setNewProduct(prev => ({ ...prev, product_name: v }))}
                  onSelectSuggestion={(s) => {
                    if (s.ean && !newProduct.ean) {
                      setNewProduct(prev => ({ ...prev, product_name: s.name, ean: s.ean }));
                    }
                  }}
                  locationId={newProduct.location_id}
                  placeholder="Produktname"
                />
              </div>

              {/* Product Image Preview - Clickable & Editable */}
              <ProductImagePreview 
                imageUrl={newProduct.image_url || newProduct.photo}
                onChangePhoto={() => handleCameraClick()}
                onChangeUrl={(url) => setNewProduct(prev => ({ ...prev, image_url: url, photo: null }))}
                onRemove={() => setNewProduct(prev => ({ ...prev, image_url: '', photo: null }))}
              />

              {/* P0.9: Bild hinzufügen Button (nur wenn KEIN Bild vorhanden) */}
              {!newProduct.image_url && !newProduct.photo && (
                <div className="space-y-1">
                  <Label className="text-sm">Produktbild (optional)</Label>
                  <Button 
                    type="button"
                    variant="outline" 
                    onClick={() => handleCameraClick()}
                    className="w-full h-10 border-dashed"
                  >
                    <Camera className="h-4 w-4 mr-2" />
                    Bild hinzufügen
                  </Button>
                </div>
              )}

              {/* P0.9: Stückzahl Feld (nur im normalen MHD-Modus, nicht im Sofort-Abschreiben) */}
              {!immediateWriteOffMode && (
                <div className="space-y-1">
                  <Label className="text-sm">Stückzahl (optional)</Label>
                  <Input
                    type="number"
                    min="1"
                    placeholder="optional"
                    value={newProduct.quantity}
                    onChange={(e) => setNewProduct(prev => ({ ...prev, quantity: e.target.value ? parseInt(e.target.value) : '' }))}
                  />
                  <p className="text-xs text-muted-foreground">Optional, nur zur Information / Statistik</p>
                </div>
              )}

              {/* Artikelnummer (optional) – NUR im normalen MHD-Modus sichtbar */}
              {!immediateWriteOffMode && (
                <div className="space-y-1">
                  <Label className="text-sm">
                    JET-Artikelnummer
                    <span className="text-muted-foreground font-normal text-xs ml-1">(optional)</span>
                  </Label>
                  <Input
                    inputMode="numeric"
                    autoComplete="off"
                    placeholder="z.B. 12345"
                    value={newProduct.article_number || ''}
                    onChange={(e) => setNewProduct(prev => ({ ...prev, article_number: e.target.value }))}
                  />
                  <p className="text-xs text-muted-foreground">
                    Einmal eingeben → wird beim nächsten Abschreiben automatisch ausgefüllt
                  </p>
                </div>
              )}

              {/* Date Picker ODER Sofort-Abschreiben */}
              <div className="space-y-2">
                <div className="flex items-center justify-between">
                  <Label className="text-sm flex items-center gap-2">
                    <CalendarDays className="h-4 w-4" />
                    {immediateWriteOffMode ? 'Sofort abschreiben' : 'Ablaufdatum *'}
                  </Label>
                  <Button
                    variant={immediateWriteOffMode ? "default" : "outline"}
                    size="sm"
                    onClick={() => setImmediateWriteOffMode(!immediateWriteOffMode)}
                    className={immediateWriteOffMode 
                      ? "h-7 text-xs bg-orange-600 hover:bg-orange-700" 
                      : "h-7 text-xs border-orange-300 text-orange-600 hover:bg-orange-50"
                    }
                  >
                    <Trash2 className="h-3 w-3 mr-1" />
                    {immediateWriteOffMode ? 'MHD erfassen' : 'Sofort abschreiben'}
                  </Button>
                </div>
                
                {immediateWriteOffMode ? (
                  // Sofort-Abschreiben Felder
                  <div className="space-y-3 p-3 bg-orange-50 dark:bg-orange-950/40 border border-orange-200 dark:border-orange-800 rounded-lg">
                    <p className="text-xs text-orange-700 dark:text-orange-400 font-medium">
                      ⚡ Produkt wird sofort als abgeschrieben erfasst (ohne MHD-Überwachung)
                    </p>
                    
                    <div className="space-y-1">
                      <Label className="text-sm">Stückzahl *</Label>
                      <Input
                        type="number"
                        min="1"
                        placeholder="Anzahl"
                        value={immediateWriteOffData.quantity}
                        onChange={(e) => setImmediateWriteOffData(prev => ({ ...prev, quantity: e.target.value }))}
                      />
                    </div>
                    
                    <div className="space-y-1">
                      <Label className="text-sm">
                        JET-Artikelnummer (WWS-Nr.) {!immediateWriteOffData.noArticleNumber && '*'}
                      </Label>
                      <Input
                        placeholder="z.B. 57978"
                        inputMode="numeric"
                        autoComplete="off"
                        value={immediateWriteOffData.article_number}
                        onChange={(e) => setImmediateWriteOffData(prev => ({ ...prev, article_number: e.target.value, _artNrManual: true }))}
                        disabled={immediateWriteOffData.noArticleNumber}
                        className={immediateWriteOffData.noArticleNumber ? 'bg-gray-100' : ''}
                      />
                      {immediateWriteOffData.article_number && !immediateWriteOffData._artNrManual && (
                        <p className="text-xs text-green-600 flex items-center gap-1">
                          ✓ Automatisch ausgefüllt – bei Bedarf änderbar
                        </p>
                      )}
                      <div className="flex items-center gap-2 mt-2">
                        <input
                          type="checkbox"
                          id="immediateNoArticleNumber"
                          checked={immediateWriteOffData.noArticleNumber}
                          onChange={(e) => setImmediateWriteOffData(prev => ({ 
                            ...prev, 
                            noArticleNumber: e.target.checked,
                            article_number: e.target.checked ? '' : prev.article_number
                          }))}
                          className="h-4 w-4 rounded border-gray-300 dark:border-gray-600"
                        />
                        <label htmlFor="immediateNoArticleNumber" className="text-sm text-muted-foreground">
                          Artikelnummer nicht vorhanden
                        </label>
                      </div>
                    </div>
                  </div>
                ) : (
                  // Normaler MHD Date Picker
                  <CalendarDatePicker
                    value={newProduct.expiry_date}
                    onChange={(date) => setNewProduct(prev => ({ ...prev, expiry_date: date }))}
                  />
                )}
              </div>

              {/* Location - wird auch für Sofort-Abschreiben benötigt */}
              <div className="space-y-1">
                <Label className="text-sm">Standort {!immediateWriteOffMode && '*'}</Label>
                <Select 
                  value={newProduct.location_id} 
                  onValueChange={(value) => setNewProduct(prev => ({ ...prev, location_id: value }))}
                >
                  <SelectTrigger>
                    <SelectValue placeholder="Standort wählen" />
                  </SelectTrigger>
                  <SelectContent>
                    {locations.map(loc => (
                      <SelectItem key={loc.id} value={loc.id}>{loc.name}</SelectItem>
                    ))}
                  </SelectContent>
                </Select>
              </div>
              
              {/* Action Buttons */}
              <div className="flex gap-2 pt-3 border-t">
                <Button 
                  variant="outline" 
                  onClick={() => setAddDialogOpen(false)} 
                  className="flex-1"
                  disabled={submitting}
                >
                  Abbrechen
                </Button>
                <Button 
                  onClick={() => handleSubmitProduct()} 
                  disabled={submitting || !newProduct.ean || !newProduct.product_name || 
                    (immediateWriteOffMode 
                      ? (!immediateWriteOffData.quantity || (!immediateWriteOffData.noArticleNumber && !immediateWriteOffData.article_number))
                      : !newProduct.expiry_date
                    )
                  }
                  className={immediateWriteOffMode ? "flex-1 bg-orange-600 hover:bg-orange-700" : "flex-1"}
                >
                  {submitting && <Loader2 className="h-4 w-4 mr-1 animate-spin" />}
                  {immediateWriteOffMode ? 'Abschreiben' : 'Speichern'}
                </Button>
              </div>
            </div>
          )}
        </DialogContent>
      </Dialog>

      {/* Unknown GTIN Dialog - NEW FAST WORKFLOW */}
      <Dialog open={unknownGtinDialogOpen} onOpenChange={(open) => {
        // Verhindere Schließen während des Speicherns
        if (!open && submitting) return;
        setUnknownGtinDialogOpen(open);
      }}>
        <DialogContent className="sm:max-w-[400px]">
          <DialogHeader>
            <DialogTitle className="flex items-center gap-2">
              <AlertCircle className="h-5 w-5 text-orange-600" />
              Produkt nicht gefunden
            </DialogTitle>
          </DialogHeader>

          <div className="space-y-4 py-2">
            <div className="p-3 bg-orange-50 dark:bg-orange-900/20 rounded-lg border border-orange-200 dark:border-orange-800">
              <p className="text-sm font-medium mb-1">GTIN: {newProduct.ean}</p>
              <p className="text-xs text-muted-foreground">
                Dieses Produkt ist noch nicht in der Datenbank. Möchten Sie es jetzt anlegen?
              </p>
            </div>

            <div className="space-y-1">
              <Label className="text-sm">Produktname *</Label>
              <ProductNameAutocomplete
                value={newProduct.product_name}
                onChange={(v) => setNewProduct(prev => ({ ...prev, product_name: v }))}
                onSelectSuggestion={(s) => {
                  if (s.ean && !newProduct.ean) {
                    setNewProduct(prev => ({ ...prev, product_name: s.name, ean: s.ean }));
                  }
                }}
                locationId={newProduct.location_id}
                placeholder="Produktname eingeben"
              />
            </div>

            {/* P0.9: Bild hinzufügen (optional) */}
            {!newProduct.image_url && !newProduct.photo && (
              <div className="space-y-1">
                <Label className="text-sm">Produktbild (optional)</Label>
                <Button 
                  type="button"
                  variant="outline" 
                  onClick={() => handleCameraClick()}
                  className="w-full h-10 border-dashed"
                >
                  <Camera className="h-4 w-4 mr-2" />
                  Bild hinzufügen
                </Button>
              </div>
            )}

            {/* P0.9: Vorschau wenn Bild hochgeladen */}
            {(newProduct.image_url || newProduct.photo) && (
              <div className="space-y-1">
                <Label className="text-sm">Produktbild</Label>
                <div className="flex items-center gap-2">
                  <img 
                    src={newProduct.image_url || newProduct.photo} 
                    alt="Preview"
                    className="h-16 w-16 object-cover rounded border"
                  />
                  <Button 
                    type="button"
                    variant="ghost" 
                    size="sm"
                    onClick={() => setNewProduct(prev => ({ ...prev, image_url: '', photo: null }))}
                  >
                    <X className="h-4 w-4 mr-1" />
                    Entfernen
                  </Button>
                </div>
              </div>
            )}

            {/* P0.9: Stückzahl */}
            <div className="space-y-1">
              <Label className="text-sm">Stückzahl (optional)</Label>
              <Input
                type="number"
                min="1"
                placeholder="optional"
                value={newProduct.quantity}
                onChange={(e) => setNewProduct(prev => ({ ...prev, quantity: e.target.value ? parseInt(e.target.value) : '' }))}
              />
              <p className="text-xs text-muted-foreground">Optional, nur zur Information / Statistik</p>
            </div>

            <div className="p-3 bg-blue-50 dark:bg-blue-900/20 rounded-lg border border-blue-200 dark:border-blue-800">
              <p className="text-xs text-blue-700 dark:text-blue-300">
                ✓ Das Produkt wird sofort für Ihren Standort verfügbar sein<br/>
                ✓ MHD-Eintrag wird erstellt (Stückzahl optional)<br/>
                ✓ Der Superadmin wird benachrichtigt und kann es für alle freigeben
              </p>
            </div>
          </div>

          <DialogFooter>
            <Button 
              variant="outline" 
              onClick={() => {
                setUnknownGtinDialogOpen(false);
                // NICHT newProduct resetten - das verursacht State-Jump
                // User kann im Hauptdialog weitermachen
              }}
              disabled={submitting}
            >
              Abbrechen
            </Button>
            <Button 
              onClick={() => handleSaveUnknownGtin()}
              disabled={submitting}
            >
              {submitting ? (
                <>
                  <Loader2 className="h-4 w-4 mr-2 animate-spin" />
                  Speichert...
                </>
              ) : (
                'Speichern & weiter'
              )}
            </Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>

      {/* Write-Off Dialog */}
      <Dialog open={writeOffDialogOpen} onOpenChange={setWriteOffDialogOpen}>
        <DialogContent className="sm:max-w-[400px]">
          <DialogHeader>
            <DialogTitle className="flex items-center gap-2">
              {writeOffData.isSold ? (
                <>
                  <CheckCircle className="h-5 w-5 text-green-600" />
                  Abverkauft
                </>
              ) : (
                <>
                  <Trash2 className="h-5 w-5 text-red-600" />
                  Abschreiben
                </>
              )}
            </DialogTitle>
          </DialogHeader>

          {selectedProduct && (
            <div className="space-y-4 py-2">
              <div className="p-3 bg-gray-50 dark:bg-gray-800 rounded-lg">
                <p className="font-semibold">{selectedProduct.product_name}</p>
                <p className="text-sm text-muted-foreground">EAN: {selectedProduct.ean}</p>
              </div>

              {/* Abverkauft Checkbox - NEU */}
              <div className={`flex items-center gap-3 p-3 rounded-lg border-2 transition-colors ${
                writeOffData.isSold ? 'bg-green-50 dark:bg-green-950/40 border-green-500 dark:border-green-600' : 'bg-gray-50 dark:bg-gray-800 border-gray-200 dark:border-gray-700'
              }`}>
                <input
                  type="checkbox"
                  id="isSold"
                  checked={writeOffData.isSold}
                  onChange={(e) => setWriteOffData(prev => ({ 
                    ...prev, 
                    isSold: e.target.checked,
                    quantity: e.target.checked ? '' : prev.quantity
                  }))}
                  className="h-5 w-5 rounded border-gray-300 dark:border-gray-600 text-green-600 focus:ring-green-500"
                />
                <label htmlFor="isSold" className={`font-medium cursor-pointer ${writeOffData.isSold ? 'text-green-700' : ''}`}>
                  Abverkauft (nicht abgeschrieben)
                </label>
              </div>

              {/* Felder - Stückzahl nur anzeigen wenn NICHT abverkauft */}
              <div className="space-y-3">
                {!writeOffData.isSold && (
                  <div className="space-y-1">
                    <Label className="text-sm">Stückzahl *</Label>
                    <Input
                      type="number"
                      inputMode="numeric"
                      min="1"
                      placeholder="Anzahl"
                      value={writeOffData.quantity}
                      onChange={(e) => setWriteOffData(prev => ({ ...prev, quantity: e.target.value }))}
                      onClick={(e) => e.currentTarget.focus()}
                    />
                  </div>
                )}

                {/* JET-Artikelnummer - IMMER sichtbar (auch bei Abverkauft) */}
                <div className="space-y-1">
                  <Label className="text-sm">
                    JET-Artikelnummer (WWS-Nr.) {!writeOffData.noArticleNumber && !writeOffData.isSold && '*'}
                  </Label>
                  <Input
                    placeholder="z.B. 57978"
                    inputMode="numeric"
                    autoComplete="off"
                    value={writeOffData.article_number}
                    onChange={(e) => setWriteOffData(prev => ({ ...prev, article_number: e.target.value, _artNrManual: true }))}
                    disabled={writeOffData.noArticleNumber}
                    className={writeOffData.noArticleNumber ? 'bg-gray-100' : ''}
                    onClick={(e) => !writeOffData.noArticleNumber && e.currentTarget.focus()}
                  />
                  {writeOffData.article_number && !writeOffData._artNrManual && (
                    <p className="text-xs text-green-600 flex items-center gap-1">
                      ✓ Automatisch ausgefüllt – bei Bedarf änderbar
                    </p>
                  )}
                  <div className="flex items-center gap-2 mt-2">
                    <input
                      type="checkbox"
                      id="noArticleNumber"
                      checked={writeOffData.noArticleNumber}
                      onChange={(e) => setWriteOffData(prev => ({ 
                        ...prev, 
                        noArticleNumber: e.target.checked,
                        article_number: e.target.checked ? '' : prev.article_number
                      }))}
                      className="h-4 w-4 rounded border-gray-300 dark:border-gray-600"
                    />
                    <label htmlFor="noArticleNumber" className="text-sm text-muted-foreground">
                      Artikelnummer nicht vorhanden
                    </label>
                  </div>
                </div>

                <div className="space-y-1">
                  <Label className="text-sm">EAN</Label>
                  <Input
                    value={selectedProduct.ean}
                    readOnly
                    inputMode="numeric"
                    className="bg-gray-100 dark:bg-gray-800 cursor-default select-all"
                    onFocus={(e) => e.target.select()}
                  />
                  <p className="text-xs text-muted-foreground">
                    Automatisch aus Produktdaten übernommen – nicht editierbar
                  </p>
                </div>
              </div>

              {/* Info-Text für Abverkauft */}
              {writeOffData.isSold && (
                <div className="p-3 bg-green-50 dark:bg-green-950/40 border border-green-200 dark:border-green-800 rounded-lg">
                  <p className="text-sm text-green-700">
                    Das Produkt wird als abverkauft markiert und erscheint in der Statistik unter &ldquo;Abverkauft&rdquo;.
                  </p>
                </div>
              )}
            </div>
          )}

          <DialogFooter>
            <Button variant="outline" onClick={() => setWriteOffDialogOpen(false)}>
              Abbrechen
            </Button>
            <Button 
              variant={writeOffData.isSold ? "default" : "destructive"}
              className={writeOffData.isSold ? "bg-green-600 hover:bg-green-700" : ""}
              onClick={handleWriteOff} 
              disabled={
                submitting || 
                (!writeOffData.isSold && !writeOffData.quantity) ||
                (!writeOffData.isSold && !writeOffData.noArticleNumber && (!writeOffData.article_number || writeOffData.article_number.trim() === ''))
              }
            >
              {submitting && <Loader2 className="h-4 w-4 mr-1 animate-spin" />}
              {writeOffData.isSold ? 'Als abverkauft markieren' : 'Abschreiben'}
            </Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>

      {/* Bulk-Cleanup Dialog: Rückstand abgelaufener Artikel abschreiben */}
      <Dialog open={bulkOpen} onOpenChange={(o) => { if (!bulkRunning) setBulkOpen(o); }}>
        <DialogContent className="sm:max-w-[480px]" data-testid="bulk-cleanup-dialog">
          <DialogHeader>
            <DialogTitle className="flex items-center gap-2">
              <Trash2 className="h-5 w-5 text-red-600" />
              Rückstand bereinigen
            </DialogTitle>
          </DialogHeader>
          {bulkLoading ? (
            <div className="py-8 flex items-center justify-center text-muted-foreground">
              <Loader2 className="h-5 w-5 animate-spin mr-2" /> Vorschau wird geladen…
            </div>
          ) : bulkPreview ? (
            <div className="space-y-3">
              <div className="p-3 rounded-lg bg-red-50 dark:bg-red-950/30 border border-red-200 dark:border-red-900/50">
                <p className="text-sm text-gray-800 dark:text-gray-100">
                  Es werden <b data-testid="bulk-count">{bulkPreview.count}</b> abgelaufene Artikel
                  (Gesamtmenge <b>{bulkPreview.total_quantity}</b>) als <b>abgeschrieben</b> markiert.
                </p>
                <p className="text-xs text-muted-foreground mt-1">
                  {selectedLocation !== 'all' ? 'Nur der aktuell gewählte Standort.' : 'Alle Standorte dieses Kunden.'}
                  {' '}Die Artikel erscheinen danach korrekt in der Statistik und nicht mehr in der Liste.
                </p>
              </div>
              {bulkPreview.count > 0 && (
                <div className="max-h-48 overflow-y-auto border rounded-lg divide-y dark:divide-gray-800">
                  {bulkPreview.sample.map((s) => (
                    <div key={s.id} className="flex items-center justify-between px-3 py-1.5 text-xs">
                      <span className="truncate mr-2">{s.product_name}</span>
                      <span className="text-muted-foreground whitespace-nowrap">
                        {s.expiry_date} · {s.quantity}×
                      </span>
                    </div>
                  ))}
                  {bulkPreview.sample_truncated && (
                    <div className="px-3 py-1.5 text-xs text-muted-foreground">… weitere werden ebenfalls bereinigt</div>
                  )}
                </div>
              )}
            </div>
          ) : (
            <p className="py-6 text-sm text-muted-foreground">Keine Daten.</p>
          )}
          <DialogFooter>
            <Button variant="outline" onClick={() => setBulkOpen(false)} disabled={bulkRunning}>
              Abbrechen
            </Button>
            <Button
              variant="destructive"
              onClick={runBulkCleanup}
              disabled={bulkRunning || bulkLoading || !bulkPreview || bulkPreview.count === 0}
              data-testid="bulk-cleanup-confirm"
            >
              {bulkRunning ? <Loader2 className="h-4 w-4 animate-spin mr-1" /> : <Trash2 className="h-4 w-4 mr-1" />}
              {bulkPreview?.count ? `${bulkPreview.count} abschreiben` : 'Abschreiben'}
            </Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>


      {/* Settings Dialog */}
      <Dialog open={settingsDialogOpen} onOpenChange={setSettingsDialogOpen}>
        <DialogContent className="sm:max-w-[350px]">
          <DialogHeader>
            <DialogTitle className="flex items-center gap-2">
              <Settings className="h-5 w-5" />
              Einstellungen
            </DialogTitle>
          </DialogHeader>

          <div className="space-y-4 py-2">
            <div className="space-y-2">
              <Label>Warnzeit (Tage)</Label>
              <Input
                type="number"
                min="1"
                max="30"
                value={settings.warning_days}
                onChange={(e) => setSettings(prev => ({ ...prev, warning_days: parseInt(e.target.value) || 7 }))}
              />
              <p className="text-xs text-muted-foreground">
                Produkte werden &ldquo;Bald ablaufend&rdquo; wenn sie innerhalb dieser Tage ablaufen
              </p>
            </div>
          </div>

          <DialogFooter>
            <Button variant="outline" onClick={() => setSettingsDialogOpen(false)}>
              Abbrechen
            </Button>
            <Button onClick={handleSaveSettings}>
              Speichern
            </Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>

      {/* Edit Product Dialog */}
      <Dialog open={editDialogOpen} onOpenChange={setEditDialogOpen}>
        <DialogContent className="sm:max-w-[400px]">
          <DialogHeader>
            <DialogTitle className="flex items-center gap-2">
              <Pencil className="h-5 w-5 text-blue-600" />
              Produkt bearbeiten
            </DialogTitle>
          </DialogHeader>

          {selectedProduct && (
            <div className="space-y-4 py-2">
              {/* Product Image Section */}
              <div className="space-y-2">
                <Label className="text-sm flex items-center gap-2">
                  <ImageIcon className="h-4 w-4" />
                  Produktbild
                </Label>
                <div className="flex items-start gap-3">
                  {editProduct.image_url ? (
                    <img 
                      src={editProduct.image_url} 
                      alt="Produkt"
                      className="w-16 h-16 object-cover rounded-lg border"
                      onError={(e) => { e.target.style.display = 'none'; }}
                    />
                  ) : (
                    <div className="w-16 h-16 bg-gray-100 dark:bg-gray-800 rounded-lg flex items-center justify-center border">
                      <Package className="h-6 w-6 text-gray-400" />
                    </div>
                  )}
                  <div className="flex-1 space-y-2">
                    <input
                      type="file"
                      accept="image/*"
                      capture="environment"
                      ref={editFileInputRef}
                      onChange={handleEditPhotoCapture}
                      className="hidden"
                    />
                    <Button 
                      variant="outline" 
                      size="sm" 
                      className="w-full"
                      onClick={() => editFileInputRef.current?.click()}
                    >
                      <Camera className="h-4 w-4 mr-1" />
                      Neues Foto
                    </Button>
                    <Button 
                      variant="outline" 
                      size="sm" 
                      className="w-full"
                      onClick={() => setEditImageMode(editImageMode === 'url' ? null : 'url')}
                    >
                      <LinkIcon className="h-4 w-4 mr-1" />
                      URL eingeben
                    </Button>
                  </div>
                </div>
                {editImageMode === 'url' && (
                  <Input
                    placeholder="https://example.com/bild.jpg"
                    value={editProduct.image_url?.startsWith('data:') ? '' : editProduct.image_url}
                    onChange={(e) => setEditProduct(prev => ({ ...prev, image_url: e.target.value }))}
                    className="mt-2"
                  />
                )}
              </div>

              <div className="space-y-2">
                <Label className="text-sm">EAN-Code</Label>
                <Input
                  placeholder="EAN-Code"
                  value={editProduct.ean}
                  onChange={(e) => setEditProduct(prev => ({ ...prev, ean: e.target.value }))}
                />
              </div>

              <div className="space-y-2">
                <Label className="text-sm">Produktname *</Label>
                <Input
                  placeholder="Produktname"
                  value={editProduct.product_name}
                  onChange={(e) => setEditProduct(prev => ({ ...prev, product_name: e.target.value }))}
                />
              </div>

              <div className="space-y-2">
                <Label className="text-sm flex items-center gap-2">
                  <CalendarDays className="h-4 w-4" />
                  Ablaufdatum *
                </Label>
                <Input
                  type="date"
                  value={editProduct.expiry_date}
                  onChange={(e) => setEditProduct(prev => ({ ...prev, expiry_date: e.target.value }))}
                />
              </div>

              {locations.length > 1 && (
                <div className="space-y-2">
                  <Label className="text-sm">Standort</Label>
                  <Select 
                    value={editProduct.location_id} 
                    onValueChange={(value) => setEditProduct(prev => ({ ...prev, location_id: value }))}
                  >
                    <SelectTrigger>
                      <SelectValue placeholder="Standort wählen" />
                    </SelectTrigger>
                    <SelectContent>
                      {locations.map(loc => (
                        <SelectItem key={loc.id} value={loc.id}>{loc.name}</SelectItem>
                      ))}
                    </SelectContent>
                  </Select>
                </div>
              )}
            </div>
          )}

          <DialogFooter>
            <Button variant="outline" onClick={() => setEditDialogOpen(false)}>
              Abbrechen
            </Button>
            <Button 
              onClick={handleEditProduct} 
              disabled={submitting || !editProduct.product_name || !editProduct.expiry_date}
            >
              {submitting && <Loader2 className="h-4 w-4 mr-1 animate-spin" />}
              Speichern
            </Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>
    </DashboardLayout>

    {/* FULLSCREEN SCANNER - AUßERHALB von allem! */}
    {scannerOpen && (
      <BarcodeScanner 
        onDetected={handleBarcodeDetected}
        onClose={() => setScannerOpen(false)}
      />
    )}

    {/* Dubletten-Warnung Modal */}
    <Dialog open={!!duplicateWarning} onOpenChange={(open) => { if (!open) { setDuplicateWarning(null); setPendingForceAction(null); } }}>
      <DialogContent className="sm:max-w-[420px]">
        <DialogHeader>
          <DialogTitle className="flex items-center gap-2 text-orange-600">
            <AlertTriangle className="h-5 w-5" />
            Produkt bereits erfasst
          </DialogTitle>
          <DialogDescription className="pt-2 text-sm text-foreground">
            <span className="font-semibold">{duplicateWarning?.product_name}</span> wurde in diesem
            Standort für dieses Datum bereits aufgenommen
            {duplicateWarning?.created_by_name ? (
              <> von <span className="font-semibold">{duplicateWarning.created_by_name}</span></>
            ) : null}.
          </DialogDescription>
        </DialogHeader>
        <div className="py-2 px-1 bg-orange-50 dark:bg-orange-900/20 border border-orange-200 dark:border-orange-800 rounded-lg text-sm text-orange-800 dark:text-orange-300">
          Möchtest du es trotzdem erneut speichern?
        </div>
        <DialogFooter className="gap-2 sm:gap-0">
          <Button
            variant="outline"
            onClick={() => { setDuplicateWarning(null); setPendingForceAction(null); }}
            data-testid="duplicate-warning-cancel"
          >
            Abbrechen
          </Button>
          <Button
            className="bg-orange-600 hover:bg-orange-700 text-white"
            onClick={() => {
              const action = pendingForceAction;
              setDuplicateWarning(null);
              setPendingForceAction(null);
              if (action) action();
            }}
            data-testid="duplicate-warning-confirm"
          >
            Trotzdem speichern
          </Button>
        </DialogFooter>
      </DialogContent>
    </Dialog>
    </>
  );
};
